import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, API, getAuthToken, signalSessionSuperseded, WS_CLOSE_SESSION_SUPERSEDED } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EloBadge from "@/components/EloBadge";
import PlayerChip from "@/components/PlayerChip";
import Avatar from "@/components/Avatar";
import { markLiveGameLeft } from "@/components/LiveGameJoin";
import { formatClock } from "@/lib/clocks";
import FeedbackModal from "@/components/FeedbackModal";
import DraggablePill from "@/components/DraggablePill";
import ReviewModal, { buildSteps, fromServerMoves } from "@/components/ReviewModal";
import GameOverModal from "@/components/GameOverModal";
import RacePodium, { racePlaces } from "@/components/RacePodium";
import RaceResult, { RaceResultPill } from "@/components/RaceResult";
import WinCelebration from "@/components/WinCelebration";
import HowToPlayDrawer from "@/components/HowToPlayDrawer";
import SettingsDialog from "@/components/SettingsDialog";
import FirstGameGuide, { shouldShowFirstGameGuide } from "@/components/FirstGameGuide";
import ResignConfirmModal from "@/components/ResignConfirmModal";
import ChatPanel from "@/components/ChatPanel";
import useKeyboardViewport from "@/lib/useKeyboardViewport";
import HeadToHead, { fetchOpponentCharacter } from "@/components/HeadToHead";
import { myCharacter } from "@/lib/avatarConfig";
import { LH_LAYOUT } from "@/lib/letterheist";
import { renderQueen, LH_ME_LOOK, LH_OPP_LOOK } from "@/lib/lhQueen";
import { LH_PAGE_BG, LhHeader, LhSettingsDialog, LhHelpDialog, useLhSound } from "@/components/LetterheistChrome";
import { renderAvatar, flagSvg, AVATAR_CSS, FEET, VIEWBOX } from "@/lib/avatar2d";
import { fetchCrowns, wearsCrown } from "@/lib/crowns";
import { crownSvg, CROWN_CSS } from "@/lib/crownSvg";
import { colourFor, resolveColours } from "@/lib/playerColours";
import {
  ArrowLeft,
  Trophy,
  Flag,
  Handshake,
  Layout,
  DotsThreeVertical,
  DotsSixVertical,
  ChatCircle,
  CaretUp,
  CaretDown,
  Play as PlayIcon,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { JangleBrand } from "@/components/JangleLogo";

// Captures this device has already shown, so a finished game's capture is
// replayed at most once.
const CAPTURE_SEEN_KEY = (id) => `jangle:capture-seen:${id}`;
function captureSeen(id) {
  try { return !!localStorage.getItem(CAPTURE_SEEN_KEY(id)); } catch { return true; }
}
function markCaptureSeen(id) {
  try { if (id) localStorage.setItem(CAPTURE_SEEN_KEY(id), "1"); } catch { /* ignore */ }
}

// How long the winner's streamers play before the result menu opens.
const STREAMERS_BEFORE_MENU_MS = 2500;

const chatSeenKey = (matchId) => `jangle:chat-seen:${matchId}`;
function readChatSeen(matchId) {
  try { return localStorage.getItem(chatSeenKey(matchId)); } catch { return null; }
}
function writeChatSeen(matchId, id) {
  try { localStorage.setItem(chatSeenKey(matchId), id); } catch { /* ignore */ }
}

const LAYOUTS = [
  { id: "classic", name: "Old Classic" },
  { id: "gauntlet", name: "Gauntlet" },
  { id: "open_field", name: "Open Field" },
  { id: "fortress", name: "Classic Fortress" },
  { id: "mini", name: "Mini" },
  { id: "boardgame", name: "Original" },
  { id: "boardgame2", name: "Fortress" },
  { id: "fortress2", name: "Fortress 2.0" },
  { id: "infinity", name: "Infinity (old)" },
  { id: "infinity2", name: "Infinity" },
  { id: "boardgame3", name: "Classic" },
  { id: "boardgame4", name: "Firefly (old)" },
  { id: "firefly2", name: "Firefly" },
  { id: "boardgame5", name: "The Wave" },
  { id: "bridges", name: "Bridges (old)" },
  { id: "bridges_trial", name: "Bridges (short)" },
  { id: "bridges2", name: "Bridges" },
  { id: "bridges3", name: "Bridges 2.0" },
  { id: "race", name: "Raider Races" },
  { id: "letterheist", name: "Letterheist" },
];

// Human-readable subtitle for the game-over modal, derived from the
// machine-readable win_type stored by the backend.
// After this many turns each, the player closest to the opponent's base
// wins (Jack, 2026-10-12). Must match TURN_LIMIT in backend/game_ws.py.
const TURN_LIMIT = 15;

// A chess clock beside a player's name: green and ticking on their turn,
// red under 30 seconds (Jack picked option A, 2026-10-23).
function ClockPill({ ms, live, testId }) {
  const low = ms !== null && ms < 30000;
  return (
    <span
      className={`flex-none font-mono font-extrabold text-[13px] leading-none tabular-nums px-[7px] py-[3px] rounded-lg border ${
        low ? "text-destructive border-destructive" : live ? "text-green-500 border-green-500" : "border-border bg-surface text-foreground"
      } ${live ? "border-2 shadow-[0_0_0_3px_rgba(34,197,94,0.15)]" : ""}`}
      data-testid={testId}
    >
      {ms === null ? "–:––" : formatClock(ms)}
    </span>
  );
}

function formatWinReason(result, winType, fallback, race = false) {
  const isWin = result === "win";
  const isLoss = result === "loss";
  // Raider Races: first to the top row (Jack, 2026-10-23).
  if (race && winType === "turns") {
    return isWin ? `${TURN_LIMIT} rounds are up and you were closest to the finish!`
      : isLoss ? `${TURN_LIMIT} rounds are up and your opponent was closer to the finish.` : fallback || "";
  }
  if (race && (winType === "play" || winType === "capture")) {
    return isWin ? "You reached the finish first!" : isLoss ? "Your opponent reached the finish first." : fallback || "";
  }
  switch (winType) {
    case "abandonment":
      if (isWin) return "Won due to opponent abandonment.";
      if (isLoss) return "You were defeated — match abandoned.";
      return "Match ended by abandonment.";
    case "resign":
      if (isWin) return "You win due to opponent resignation.";
      if (isLoss) return "You lost due to resignation.";
      return "Match ended by resignation.";
    case "capture":
      if (isWin) return "You broke into their base and took the flag!";
      if (isLoss) return "Opponent broke into your base and took your flag.";
      return fallback || "";
    case "turns":
      if (isWin) return `${TURN_LIMIT} turns each are up and you were closest to their base!`;
      if (isLoss) return `${TURN_LIMIT} turns each are up and your opponent was closer to your base.`;
      return fallback || "";
    case "sealed":
      if (isWin) return "Your opponent was sealed off, so you win on proximity!";
      if (isLoss) return "You were sealed off, so your opponent wins on proximity.";
      return fallback || "";
    // A chess clock ran out: whoever is closer to the other base wins, not
    // just the player with time left (Jack, 2026-10-23).
    case "timeout":
      if (isWin) return "Time ran out and you were closer to their base!";
      if (isLoss) return "Time ran out and your opponent was closer to your base.";
      return fallback || "";
    case "draw":
      return "You both agreed to a draw.";
    case "play":
      if (isWin) return "You reached the opponent's base!";
      if (isLoss) return "Opponent reached your base.";
      return fallback || "";
    default:
      return fallback || "";
  }
}

export default function Game() {
  const { matchId } = useParams();
  const [searchParams] = useSearchParams();
  const initialLayout = searchParams.get("layout") || "classic";
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const performResignRef = useRef(null);
  const { user, refreshUser } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const toggleThemeRef = useRef(toggleTheme);
  toggleThemeRef.current = toggleTheme;
  const iframeRef = useRef(null);
  // Letterheist: board-game sounds on or off (its Settings).
  const lhSound = useLhSound(iframeRef);
  const lhMutedRef = useRef(lhSound.muted);
  lhMutedRef.current = lhSound.muted;
  // The theme the game iframe was first loaded with. It must not be part of
  // the iframe src after that: changing src reloads the whole game mid-match
  // (this is what broke the board when a player toggled light/dark on a
  // phone). Later theme changes go over postMessage instead.
  const iframeThemeRef = useRef(theme);
  const iframeEloRef = useRef(null);

  const [match, setMatch] = useState(null);
  // A 3-player Raider Race (Jack, 2026-10-25; backend race3.py): my racer
  // number (pads left to right) and the other two, 'A' and 'B' on the board.
  const race3 = useMemo(() => {
    if (!match?.player3_id || !user?.id) return null;
    const ids = [match.player1_id, match.player2_id, match.player3_id];
    const names = [match.player1_username, match.player2_username, match.player3_username];
    const slot = ids.indexOf(user.id);
    const [i, j] = [0, 1, 2].filter((k) => k !== slot);
    return {
      slot,
      a: { id: ids[i], username: names[i] },
      b: { id: ids[j], username: names[j] },
      who: { [ids[i]]: "A", [ids[j]]: "B" },
    };
  }, [match?.player3_id, match?.player1_id, match?.player2_id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const race3Ref = useRef(null);
  race3Ref.current = race3;
  const [layout, setLayout] = useState(initialLayout);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // The board's shape, read from the game when the replay opens.
  const [reviewBoard, setReviewBoard] = useState(null);
  useEffect(() => {
    if (!reviewOpen) return;
    try { setReviewBoard(iframeRef.current?.contentWindow?.JangleBoardInfo?.() || null); }
    catch { setReviewBoard(null); }
  }, [reviewOpen]);
  const [gameOverOpen, setGameOverOpen] = useState(false);
  const [completionInfo, setCompletionInfo] = useState(null); // {result, winReason}
  const completionRef = useRef(null);
  completionRef.current = completionInfo;
  // Game stats on the end pop-up (Jack, 2026-11-04): worked out by the board
  // page from its turn-by-turn history; games against people also keep them
  // on the server (once per game per player).
  const [gameStats, setGameStats] = useState(null);
  const statsSentRef = useRef(null);
  useEffect(() => { setGameStats(null); }, [matchId]);
  useEffect(() => {
    if (!gameOverOpen) return undefined;
    const read = (send) => {
      let st = null;
      try { st = iframeRef.current?.contentWindow?.JangleGameStats?.() || null; } catch { st = null; }
      if (!st) return;
      setGameStats(st);
      if (send && match && !match.is_bot && match.layout !== "race" && statsSentRef.current !== matchId) {
        statsSentRef.current = matchId;
        api.post(`/matches/${matchId}/game_stats`, st).catch(() => { statsSentRef.current = null; });
      }
    };
    read(false);
    // The last move can land on the board a moment after the pop-up opens,
    // so the server gets the second reading.
    const t = setTimeout(() => read(true), 1500);
    return () => clearTimeout(t);
  }, [gameOverOpen, matchId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Resign-confirm modal. `exiting` distinguishes the two callers:
  //   • 'exit'   → user tapped the back arrow on a live match
  //   • 'resign' → user tapped the explicit Resign button
  const [resignModal, setResignModal] = useState({ open: false, exiting: false });
  // Who has an open draw offer in this game (a user id), or null.
  const [drawOfferFrom, setDrawOfferFrom] = useState(null);
  const [eloDelta, setEloDelta] = useState(null);
  const [wsLive, setWsLive] = useState(false);
  // Player chat (human matches). History arrives from the server on connect.
  const [chatMessages, setChatMessages] = useState([]);
  const [chatOpen, setChatOpen] = useState(false);
  const keyboardBox = useKeyboardViewport();
  const [chatUnread, setChatUnread] = useState(0);
  // The header can fold into a floating button showing both players
  // (Jack, 2026-10-19, option C). Remembered on this device.
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    try { return localStorage.getItem("jangle:header-collapsed") === "1"; } catch { return false; }
  });
  const setHeaderFolded = (v) => {
    setHeaderCollapsed(v);
    try { localStorage.setItem("jangle:header-collapsed", v ? "1" : "0"); } catch { /* ignore */ }
  };
  const [helpOpen, setHelpOpen] = useState(false);
  // Settings, opened from the ⚙ button under the board (Jack, 2026-10-20).
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Header Play button mirrors the page's own Play button (Jack, 2026-10-19).
  const [canPlay, setCanPlay] = useState(false);
  const chatOpenRef = useRef(false);
  // Stream: its header shows a Messages button in games with a person, with
  // the unread count (Jack, 2026-11-04). Sent again every few seconds so a
  // freshly loaded board picks it up.
  useEffect(() => {
    if (!match || match.ui !== "stream") return undefined;
    const send = () => { try { iframeRef.current?.contentWindow?.postMessage({ type: "jangle:chat_state", on: !match.is_bot, unread: chatOpen ? 0 : chatUnread }, "*"); } catch { /* ignore */ } };
    send();
    const t = setInterval(send, 3000);
    return () => clearInterval(t);
  }, [match?.ui, match?.is_bot, chatUnread, chatOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  chatOpenRef.current = chatOpen;
  const chatIdsRef = useRef(new Set());
  // Remember the newest message shown while the panel is open.
  useEffect(() => {
    if (!chatOpen || !chatMessages.length) return;
    writeChatSeen(matchId, chatMessages[chatMessages.length - 1].id);
  }, [chatOpen, chatMessages, matchId]);
  // Rematch negotiation state: "idle" | "pending" | "incoming" | "declined"
  const [rematchState, setRematchState] = useState("idle");
  // Show the mobile keyboard toggle on touch / coarse-pointer / narrow
  // viewports. Detected at mount.
  const [showMobileKb, setShowMobileKb] = useState(false);
  const [mobileKbOpen, setMobileKbOpen] = useState(false);
  useEffect(() => {
    try {
      const touch = ("ontouchstart" in window) || (navigator.maxTouchPoints || 0) > 0;
      const coarse = window.matchMedia?.("(pointer: coarse)").matches;
      const narrow = window.innerWidth <= 900;
      const forced = new URLSearchParams(window.location.search).get("mobilekb") === "1";
      setShowMobileKb(!!(touch || coarse || narrow || forced));
    } catch { /* ignore */ }
  }, []);
  // The iframe's dock posts state changes so we can hide our "Type" pill
  // while it's open (and show it again on close).
  useEffect(() => {
    function onMsg(e) {
      const d = e?.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "jangle:mobile_kb_state") setMobileKbOpen(!!d.open);
      if (d.type === "jangle:open_help") setHelpOpen(true);
      if (d.type === "jangle:play_state") setCanPlay(!!d.on);
      if (d.type === "jangle:toggle_theme") toggleThemeRef.current?.();
      if (d.type === "jangle:open_settings") setSettingsOpen(true);
      if (d.type === "lh:ready") lhSound.sendMute(lhMutedRef.current);
      // Stream look (match.ui, Jack 2026-11-04): its own header has Back and
      // Resign (it asks first in its own pop-up).
      if (d.type === "jangle:ui_back") navigateRef.current?.("/lobby");
      if (d.type === "jangle:ui_resign_now") performResignRef.current?.(false);
      if (d.type === "jangle:ui_chat") { setChatOpen((o) => !o); setChatUnread(0); }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  // Shared round state — advances only when BOTH players have peeled.
  const [round, setRound] = useState(1);
  // Warn as the turn limit nears (once per round).
  const turnWarnedRef = useRef(0);
  const peelStateRef = useRef({ me: false, opp: false });

  // A bot game picked up again from "Your games": load where it was left and
  // hand it to the page once it has loaded (sendToIframe queues it).
  const botRestoredRef = useRef(null);
  useEffect(() => {
    if (!match?.is_bot || match.status !== "active" || botRestoredRef.current === match.match_id) return;
    botRestoredRef.current = match.match_id;
    api.get(`/match/${match.match_id}/bot-state`).then(({ data }) => {
      // Nothing saved yet: a fresh game, so a bot picked to start opens it.
      // Same time: the board waits to hear there's nothing saved before the first turn.
      if (!data?.state) { if (match.bot_first || match.simultaneous) sendToIframe({ type: "jangle:bot_opens" }); return; }
      sendToIframe({ type: "jangle:bot_restore", state: data.state });
      if (data.state.gs?.round) setRound(data.state.gs.round);
    }).catch(() => { /* a fresh game, or nothing saved yet */ });
  }, [match?.match_id, match?.is_bot, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: post a message into the iframe, queueing if it isn't ready yet.
  // A live game waiting for both players keeps the board shut: only the
  // waiting card's own lock gets through (see liveWaiting below).
  const liveWaitingRef = useRef(false);
  const sendToIframe = useCallback((payload) => {
    if (liveWaitingRef.current && payload?.type === "jangle:set_waiting" && !payload.live) return;
    const w = iframeRef.current?.contentWindow;
    if (w && iframeReadyRef.current) {
      try { w.postMessage(payload, "*"); } catch { /* ignore */ }
    } else {
      iframeInboxRef.current.push(payload);
    }
  }, []);

  // The turn number beside the arrows under the board.
  const roundRef = useRef(round);
  roundRef.current = round;
  useEffect(() => { sendToIframe({ type: "jangle:round", round }); }, [round, sendToIframe]);

  // Helper: send a WS message, queueing if the socket isn't open yet.
  const wsSendOrQueue = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try { ws.send(JSON.stringify(payload)); return true; } catch { /* ignore */ }
    }
    outboxRef.current.push(payload);
    return false;
  }, []);

  // ───── Game clock (time control) ─────
  // ── Server-authoritative timer state ────────────────────────────────
  // For real-time matches, the SERVER stamps when each round starts and
  // how long it lasts. The client ticks down by recomputing from these
  // stamps each second — both players see the SAME number, no local
  // setInterval drift can desync them.
  // Layout: { startedAtMs, durationMs, serverOffsetMs }
  //   serverOffsetMs = (client receive time) - (server now)
  //   so localNow + serverOffsetMs ≈ serverNow at any moment
  const serverTimerRef = useRef(null);
  // Raider Races start together (Jack, 2026-10-25): until every racer is
  // past the head-to-head intro the server holds the first round's clock
  // and the board stays shut. `raceWaiting` = the usernames still to come
  // (null once the race is on).
  const [raceWaiting, setRaceWaiting] = useState(null);
  const raceWaitingRef = useRef(null);
  raceWaitingRef.current = raceWaiting;
  // The race begins at a set server time (go_at_ms) after a 3-2-1 count, so
  // every racer unlocks at the same moment (Jack, 2026-11-01). `raceGoAt` is
  // that moment on this device's clock while counting down.
  const [raceGoAt, setRaceGoAt] = useState(null);
  // This device's clock minus the server's, from the ping with the shortest
  // round trip (half of it is taken off, so a slow link doesn't start late).
  const raceClockRef = useRef(null);

  const computeSecondsLeftFromServer = useCallback(() => {
    const t = serverTimerRef.current;
    if (!t) return null;
    if (raceWaitingRef.current) return Math.ceil(t.durationMs / 1000);
    const serverNow = Date.now() - t.serverOffsetMs;
    const remainMs = t.startedAtMs + t.durationMs - serverNow;
    return Math.max(0, Math.ceil(remainMs / 1000));
  }, []);

  // Real-time: timer resets every round (when `round` increments).
  // Turn-based: timer resets after every peel (either player) AND after a turn yield.
  const tcSeconds = match?.time_control_seconds || (match?.mode === "realtime" ? 120 : 60);
  // Turn-based "None" time control: time_control_seconds === 0 → no clock, no auto-peel.
  const noTimeLimit = match?.mode === "turn_based" && match?.time_control_seconds === 0;
  // Chess-style clocks (Jack, 2026-10-23): each player's time bank runs only
  // on their turn and the server ends the game when one hits zero. The
  // per-move countdown and its auto-Play are off in these games.
  const isClock = !!match?.clock;
  const [clock, setClock] = useState(null); // {ms: {uid: ms}, active, at, running}
  const [clockNow, setClockNow] = useState(() => Date.now());
  // The clocks wait until both players have been in the game together
  // (Jack, 2026-10-27); the server says so with waiting.
  const adoptClock = useCallback((c) => {
    // ticking: whose clocks run (simultaneous games: everyone who hasn't
    // played this turn yet). Older servers only say who is to move.
    if (c && c.ms) setClock({ ms: c.ms, active: c.active, ticking: Array.isArray(c.ticking) ? c.ticking : null, running: c.running !== false, waiting: !!c.waiting, at: Date.now() });
  }, []);
  const adoptClockRef = useRef(adoptClock);
  adoptClockRef.current = adoptClock;
  // Until the server's first word, start from the saved match.
  useEffect(() => {
    if (!isClock || clock || !match?.clock_ms) return;
    const started = match.clock_turn_started_ms;
    const ms = { ...match.clock_ms };
    if (started && match.turn_user_id in ms) ms[match.turn_user_id] -= Math.max(0, Date.now() - started);
    setClock({ ms, active: match.turn_user_id, running: !!started, waiting: !started, at: Date.now() });
  }, [isClock, match?.match_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isClock || match?.status !== "active") return undefined;
    const id = setInterval(() => setClockNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [isClock, match?.status]);
  const clockTicking = (uid) => !!clock && clock.running && match?.status === "active"
    && (clock.ticking ? clock.ticking.includes(uid) : uid === clock.active);
  const clockLeft = (uid) => {
    if (!clock || !(uid in clock.ms)) return null;
    return Math.max(0, clock.ms[uid] - (clockTicking(uid) ? clockNow - clock.at : 0));
  };
  // Simultaneous timed game (Jack, 2026-11-02): both players play each turn
  // at once (a real-time game with chess clocks on an ordinary board).
  const isSimul = match?.mode === "realtime" && match?.layout !== "race" && !match?.is_bot;
  // The board's height, from the game page: the server needs it to match up
  // squares between the two mirrored boards in a simultaneous game.
  const boardRowsRef = useRef(null);
  const [secondsLeft, setSecondsLeft] = useState(tcSeconds);
  // ───── Turn state (turn-based only) ─────
  // Conventionally player1 (the one stored as `player1_id`) takes the first turn.
  const [activeTurn, setActiveTurn] = useState(null); // user_id of active player, or null
  // Mirror for WS/iframe handlers, whose closures would otherwise see a stale value.
  const activeTurnRef = useRef(null);
  activeTurnRef.current = activeTurn;
  // Re-snapshot the initial value once the match doc loads.
  useEffect(() => {
    if (match?.time_control_seconds) setSecondsLeft(match.time_control_seconds);
    else if (match?.mode) setSecondsLeft(match.mode === "realtime" ? 120 : 60);
  }, [match?.time_control_seconds, match?.mode]);
  // Initialise turn-state on match load (turn-based only).
  useEffect(() => {
    if (!match || match.is_bot) return;
    if (match.mode === "turn_based" && activeTurn === null) {
      // The match doc knows whose turn it is (a game reopened later may be
      // on either player); the server's resume confirms it shortly after.
      setActiveTurn(
        [match.player1_id, match.player2_id].includes(match.turn_user_id)
          ? match.turn_user_id : match.player1_id
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.match_id, match?.mode, match?.player1_id]);
  // Reset on round change for real-time mode.
  // Guard: if serverTimerRef is already populated (e.g. we just reconnected after
  // backgrounding and the resume handler set the correct server-authoritative time),
  // do NOT overwrite it with the full duration — that would cause desync.
  useEffect(() => {
    if (match?.mode === "realtime" && !serverTimerRef.current) {
      setSecondsLeft(match?.time_control_seconds || 120);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round]);
  // Tick down once per second while the match is active.
  // Real-time mode: COMPUTE remaining from server stamps each tick (no drift).
  // Turn-based mode: simple local decrement (single active player anyway).
  useEffect(() => {
    if (!match || match.status !== "active") return;
    // Track last tick time. If a tick fires after a long gap (browser
    // throttled or the OS suspended the tab), we know the WS is likely
    // dead/stale and force a reconnect to re-baseline the timer. This
    // is more reliable than visibilitychange (which doesn't fire on
    // screen lock on many platforms, especially desktop Windows/macOS).
    let lastTickAt = Date.now();
    const WAKE_GAP_MS = 3500; // > 3 ticks missed → assume sleep/throttle
    const onWake = () => {
      try {
        // Reconnect in every mode: a socket that died while the phone was
        // asleep silently drops request_resync, and the fresh connection's
        // `resume` carries the authoritative round, timer and turn.
        if (wsRef.current) {
          try { wsRef.current.close(); } catch { /* ignore */ }
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = setTimeout(connectWs, 50);
        } else {
          try { wsSendOrQueue({ action: "request_resync" }); } catch { /* ignore */ }
        }
      } catch { /* ignore */ }
    };
    let id = setInterval(() => {
      const now = Date.now();
      if (now - lastTickAt > WAKE_GAP_MS) {
        // Tab was asleep / throttled. Force a WS reconnect to re-pull
        // the authoritative round_started_at_ms. This protects against
        // desktop screen-lock scenarios where visibilitychange does NOT
        // fire when the screen wakes up.
        onWake();
      }
      lastTickAt = now;
      if (match.mode === "realtime" && serverTimerRef.current) {
        const s = computeSecondsLeftFromServer();
        if (s !== null) setSecondsLeft(s);
      } else if (!(match.mode === "turn_based" && match.time_control_seconds === 0) && !raceWaitingRef.current) {
        setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
      }
    }, 1000);
    // ── Tab-visibility resync ────────────────────────────────────────
    // Browsers throttle setInterval in backgrounded tabs (Chrome: ~1/min).
    // When the tab becomes visible again, the LOCAL serverTimerRef may
    // be stale (the round advanced on the server while we were throttled
    // and we missed the round_advance broadcast). Two-step resync:
    //   (a) Recompute from local stamps immediately (covers the common
    //       case where the round didn't advance).
    //   (b) Ask the server for a fresh resync to overwrite stale stamps
    //       if the round actually advanced while we were asleep.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // ── Force WS reconnect on visibility restoration ──
      // Mobile OSes commonly suspend backgrounded sockets WITHOUT firing
      // onclose, so the socket appears `readyState === 1` while actually
      // being dead. Any `request_resync` sent through such a socket is
      // silently dropped → the client keeps showing a stale timer baseline
      // and desyncs from the opponent.
      // The robust fix is to TEAR DOWN and reconnect: onopen will trigger
      // the server's `resume` push which re-baselines `serverTimerRef`.
      // Until that resume arrives, we freeze the displayed seconds to the
      // current value (no negative drift, no false-positive expiry).
      try {
        if (wsRef.current) {
          try { wsRef.current.close(); } catch { /* ignore */ }
          // onclose handler auto-reconnects in 1.5s; nudge it sooner.
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = setTimeout(connectWs, 50);
        } else {
          // No WS yet: just request a resync.
          try { wsSendOrQueue({ action: "request_resync" }); } catch (e) { /* ignore */ }
        }
      } catch (e) { /* ignore */ }
      // Reset the interval to ensure fresh ticking after the throttle.
      clearInterval(id);
      lastTickAt = Date.now();
      id = setInterval(() => {
        const now2 = Date.now();
        if (now2 - lastTickAt > WAKE_GAP_MS) onWake();
        lastTickAt = now2;
        if (match.mode === "realtime" && serverTimerRef.current) {
          const s = computeSecondsLeftFromServer();
          if (s !== null) setSecondsLeft(s);
        } else if (!(match.mode === "turn_based" && match.time_control_seconds === 0)) {
          setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
        }
      }, 1000);
    };
    // Hidden (phone locked, app backgrounded): tell the server so the
    // "your turn" phone notification still goes out. Coming back
    // reconnects the socket, which marks us present again.
    const onHidden = () => {
      if (document.visibilityState !== "hidden") return;
      try {
        const ws = wsRef.current;
        if (ws && ws.readyState === 1) ws.send(JSON.stringify({ action: "away" }));
      } catch (e) { /* ignore */ }
    };
    document.addEventListener("visibilitychange", onHidden);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // Mobile Safari + Chrome use bfcache — `pageshow` fires when the page
    // is restored from cache after a tab switch / app backgrounding cycle.
    window.addEventListener("pageshow", onVisible);
    // ── Periodic heartbeat resync ──────────────────────────────────────
    // Tightened from 30s → 10s. With force-close-on-visible above, a dead
    // socket is normally detected on focus return; the 10s heartbeat is
    // the safety net for foregrounded tabs whose WS quietly died between
    // round_advance broadcasts (e.g. flaky mobile network).
    const heartbeatId = (match?.mode === "realtime")
      ? setInterval(() => {
          try { wsSendOrQueue({ action: "request_resync" }); } catch (e) { /* ignore */ }
        }, 10000)
      : null;
    return () => {
      clearInterval(id);
      if (heartbeatId) clearInterval(heartbeatId);
      document.removeEventListener("visibilitychange", onHidden);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match?.status, match?.match_id, match?.mode, computeSecondsLeftFromServer]);
  // Helper for callers (peel handlers) to reset the turn-based timer
  const resetTurnTimer = useCallback(() => {
    if (match?.mode === "turn_based") {
      setSecondsLeft(match?.time_control_seconds || 60);
    }
  }, [match?.mode, match?.time_control_seconds]);

  // ───── Auto-advance flows (timer expiry) ─────
  // When the clock hits 0 we forcibly peel the player(s) who haven't yet:
  //   • Real-time : every player who hasn't peeled this round force-peels
  //                 themselves locally → standard peel relay fires → round
  //                 advances naturally when both peels are in.
  //   • Turn-based: the ACTIVE player force-peels → standard peel relay
  //                 fires → opp's turn starts.
  // The iframe's `jangle:force_peel` handler refills the hand bypassing
  // word validation, then posts `local_peel` so the existing peel pipeline
  // does the rest (round / turn-state advance, opp relay, etc).
  const expiryHandledForRoundRef = useRef(-1);
  const expiryHandledForTurnRef = useRef(null);
  useEffect(() => {
    // Bot matches don't auto-peel from this React-side timer — the iframe's
    // local AI loop handles pacing. The timer is shown purely as a visual
    // round-clock for UI parity with human matches.
    if (!match || match.is_bot || match.status !== "active") return;
    if (noTimeLimit || isClock) return;
    if (secondsLeft !== 0) return;
    if (match.mode === "realtime") {
      // A race whose clock hasn't started (someone is still on the intro).
      if (raceWaitingRef.current) return;
      // Once per round.
      if (expiryHandledForRoundRef.current === round) return;
      expiryHandledForRoundRef.current = round;
      // Only auto-peel if we haven't peeled this round. Tagged with the
      // round, so a late one can't play the round after.
      if (!peelStateRef.current.me) {
        sendToIframe({ type: "jangle:force_peel", round });
      }
      // Safety relay (humans only — bots have no WS opponent).
      wsSendOrQueue({ action: "relay", relay_action: "time_up", detail: { round } });
    } else {
      // Turn-based: only the active player auto-peels (avoids double-fire).
      const key = `${activeTurn}-${secondsLeft}-${match.match_id}`;
      if (expiryHandledForTurnRef.current === key) return;
      expiryHandledForTurnRef.current = key;
      if (activeTurn === user?.id) {
        sendToIframe({ type: "jangle:force_peel" });
      } else {
        // Safety: nudge the opponent in case their clock drifted.
        wsSendOrQueue({ action: "relay", relay_action: "time_up", detail: {} });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, match?.mode, match?.is_bot, match?.status, match?.match_id, round, activeTurn, user?.id]);

  // Gate the iframe input for the non-active player in turn-based mode.
  // `turnLock` makes the iframe keep the overlay up through round advances
  // and auto-peel dismissals until we unlock it here.
  // `lockOnly` re-asserts the overlay without ever lifting it, so repeat
  // calls can't unblock a peel that is still being checked.
  const applyTurnGate = useCallback((lockOnly = false) => {
    if (!match || match.is_bot || match.mode !== "turn_based") return;
    const turn = activeTurnRef.current;
    if (turn === null) return;
    if (match.status !== "active") return;
    const myUid = user?.id;
    const oppUsername = match.player1_id === myUid ? match.player2_username : match.player1_username;
    if (turn === myUid) {
      if (!lockOnly) sendToIframe({ type: "jangle:set_waiting", on: false, turnLock: false });
    } else {
      sendToIframe({
        type: "jangle:set_waiting", on: true, turnLock: true,
        label: `${oppUsername || "Opponent"}'s turn<br><span style="font-size:10px;opacity:0.7;font-weight:400">your move starts when they play</span>`,
      });
    }
  }, [match?.mode, match?.is_bot, match?.status, user?.id, match?.player1_id, match?.player2_username, match?.player1_username, sendToIframe]);
  useEffect(() => {
    applyTurnGate();
  }, [activeTurn, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // Live (timed) games against a person don't start until both players
  // are in the game (Jack, 2026-11-02): the board stays shut, the clocks
  // stay frozen and a card offers a way back to the lobby. The server says
  // waiting until it has had both players in the game together.
  const liveWaiting = isClock && !!match && !match.is_bot && match.layout !== "race"
    && match.status === "active" && !!clock?.waiting;
  liveWaitingRef.current = liveWaiting;
  const [oppOnline, setOppOnline] = useState(null);
  useEffect(() => {
    if (!liveWaiting) return undefined;
    const shut = () => sendToIframe({ type: "jangle:set_waiting", on: true, turnLock: true, live: true, label: "Waiting for your opponent\u2026" });
    shut();
    const id = setInterval(shut, 2000);
    let stop = false;
    const ask = () => api.get(`/matches/${match.match_id}/opponent-online`)
      .then(({ data }) => { if (!stop) setOppOnline(!!data?.online); })
      .catch(() => { /* the card just leaves the line out */ });
    ask();
    const poll = setInterval(ask, 10000);
    return () => {
      stop = true;
      clearInterval(id);
      clearInterval(poll);
      // Both players are in: open the board (the turn gate shuts it again
      // for whoever isn't to move).
      liveWaitingRef.current = false;
      sendToIframe({ type: "jangle:set_waiting", on: false, turnLock: false });
      applyTurnGate();
    };
  }, [liveWaiting, match?.match_id]); // eslint-disable-line react-hooks/exhaustive-deps
  // "Your turn" cue: chime + green pop-up in the game whenever the turn
  // passes to this player. The first turn of a load is only shown (no chime,
  // the start flourish already plays) and waits a moment so the server's
  // resume can correct the local guess first.
  const prevTurnRef = useRef(null);
  useEffect(() => {
    if (!match || match.is_bot || match.mode !== "turn_based" || match.status !== "active") return;
    const myUid = user?.id;
    const prev = prevTurnRef.current;
    prevTurnRef.current = activeTurn;
    if (!myUid || activeTurn !== myUid || prev === myUid) return;
    if (prev !== null) {
      sendToIframe({ type: "jangle:your_turn", sound: true });
      return;
    }
    const id = setTimeout(() => {
      if (activeTurnRef.current === myUid) sendToIframe({ type: "jangle:your_turn", sound: false });
    }, 2500);
    return () => clearTimeout(id);
  }, [activeTurn, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // Safety net: re-assert the overlay every couple of seconds so nothing
  // inside the iframe (reload, round advance, wake from sleep) can leave
  // the non-active player without it.
  useEffect(() => {
    const id = setInterval(() => applyTurnGate(true), 2000);
    return () => clearInterval(id);
  }, [applyTurnGate]);
  // Server's word on whose turn it is (turn-based only).
  const adoptServerTurn = useCallback((turn) => {
    if (match?.mode !== "turn_based" || !turn) return;
    if (turn !== activeTurnRef.current) {
      setActiveTurn(turn);
      setSecondsLeft(match?.time_control_seconds || 60);
    } else {
      applyTurnGate(true);
    }
  }, [match?.mode, match?.time_control_seconds, applyTurnGate]);
  const adoptServerTurnRef = useRef(adoptServerTurn);
  adoptServerTurnRef.current = adoptServerTurn;

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  // Outbox: any relay messages queued while WS isn't open get flushed on connect.
  const outboxRef = useRef([]);
  // Inbox: messages from the iframe queued while the iframe might not have its
  // own listeners attached yet (e.g. early relay arrivals during load).
  const iframeInboxRef = useRef([]);
  const iframeReadyRef = useRef(false);
  // Hands a freshly loaded game page what it needs (bootIframe below).
  const bootIframeRef = useRef(() => {});
  const bootDocRef = useRef(null);
  const bootTimerRef = useRef(null);
  // A phone's socket can die without closing (network change, app paused
  // with the game on screen): it still says it is open, sends go nowhere
  // and nothing comes in. A Play sent through one is lost, so the player
  // sees "their turn" while the server and the opponent still wait for
  // them (admin vs Leeshy, 2026-10-29). These drop the socket and open a
  // fresh one; `resume` on the new one carries the real turn and board.
  const connectWsRef = useRef(() => {});
  const lastWsMsgAtRef = useRef(0);
  const reconnectNow = () => {
    const old = wsRef.current;
    if (old) {
      old.onclose = null;
      try { old.close(); } catch { /* ignore */ }
    }
    wsRef.current = null;
    setWsLive(false);
    clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(() => connectWsRef.current(), 50);
  };
  // Turn-based games against people: the Play the server hasn't confirmed
  // yet ({payload, tries, timer, serverTurn}). Until a "turn" message says
  // the turn has passed, it is sent again on a fresh socket, for as long as
  // it takes, and the board says so. It never quietly gives up: a Play the
  // server turns down comes back as "play_refused" (Jack: make sure this
  // never happens again, 2026-10-29).
  const pendingPlayRef = useRef(null);
  const PLAY_CONFIRM_MS = 6000;
  const PLAY_RETRY_MAX_MS = 30000;
  const armPlayCheck = () => {
    const p = pendingPlayRef.current;
    if (!p) return;
    clearTimeout(p.timer);
    p.timer = setTimeout(() => {
      if (pendingPlayRef.current !== p) return;
      p.tries += 1;
      sendToIframe({
        type: "jangle:set_waiting", on: true, turnLock: true,
        label: `Sending your move\u2026<br><span style="font-size:10px;opacity:0.7;font-weight:400">reconnecting to the server</span>`,
      });
      // `retry` lets the server drop it if the turn it belonged to is gone.
      outboxRef.current.push({ ...p.payload, detail: { ...p.payload.detail, retry: true } });
      reconnectNow();
      armPlayCheck();
    }, Math.min(PLAY_CONFIRM_MS * (p.tries + 1), PLAY_RETRY_MAX_MS));
  };
  const playConfirmed = () => {
    const p = pendingPlayRef.current;
    if (!p) return;
    clearTimeout(p.timer);
    pendingPlayRef.current = null;
    // Take down "Sending your move" if it went up.
    if (p.tries) applyTurnGate();
  };
  // Move log for the post-game review modal.
  const movesRef = useRef([]); // array of {actor, action, detail, ts}
  // Bot games: the whole game for the game record, sent by the page as it ends.
  const botRecordRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/match/${matchId}`);
        // A live game called off before it started (nobody won).
        if (data?.status === "cancelled") {
          toast("That game was called off before it started.");
          navigate("/lobby");
          return;
        }
        setMatch(data);
        if (data.layout) setLayout(data.layout);
      } catch (e) {
        toast.error("Match not found");
        navigate("/lobby");
      } finally {
        setLoading(false);
      }
    })();
  }, [matchId]); // eslint-disable-line

  // The opponent's raider (the result pop-up shows them if they win).
  const [oppCharacter, setOppCharacter] = useState(null);
  const [opp2Character, setOpp2Character] = useState(null);
  // Avatars on the board: the game iframe reads the drawing code and both
  // characters from here (see JangleBoardAvatars in jangle.html).
  useEffect(() => {
    if (!match || !user) return undefined;
    const isP1 = match.player1_id === user.id;
    const opp = race3 ? race3.a : {
      id: isP1 ? match.player2_id : match.player1_id,
      username: isP1 ? match.player2_username : match.player1_username,
      isBot: match.is_bot,
    };
    let alive = true;
    let retry = null;
    // Letterheist: a yellow queen for you and a blue one for your opponent,
    // with no flags (lib/lhQueen.js).
    const lh = match.layout === LH_LAYOUT;
    // The leader's crown floats over a raider only in the kind of game they
    // lead: Raider Races or regular Jangle (Jack, 2026-10-29), and in Live
    // Games the Live Games leader too (2026-11-02).
    const crownCat = match.layout === "race" ? "race" : lh ? null : match.clock ? ["jangle", "live"] : "jangle";
    const load = (tries) => Promise.all([
      fetchOpponentCharacter(opp),
      race3 ? fetchOpponentCharacter(race3.b) : null,
      crownCat ? fetchCrowns() : {},
    ]).then(([oppChar, opp2Char, crowns]) => {
      if (!alive) return;
      if (lh) oppChar = { ...oppChar, fallback: false, look: LH_OPP_LOOK };
      setOppCharacter(oppChar);
      setOpp2Character(opp2Char);
      window.JangleAvatars = {
        render: (look, opts) => (look?.queen ? renderQueen(look.queen) : renderAvatar(look, opts)),
        flag: lh ? () => "" : flagSvg, css: AVATAR_CSS, feet: FEET, viewBox: VIEWBOX,
        me: lh ? LH_ME_LOOK : myCharacter(user).look, opp: oppChar.look,
        ...(opp2Char ? { opp2: opp2Char.look } : {}),
        crown: crownSvg("board"), crownCss: CROWN_CSS,
        crowned: {
          me: wearsCrown(crowns, user.id, crownCat, user),
          opp: !opp.isBot && wearsCrown(crowns, opp.id, crownCat, user),
          opp2: !!race3 && wearsCrown(crowns, race3.b?.id, crownCat, user),
        },
      };
      // Their raider couldn't be fetched: keep trying, and the board swaps
      // to the right one when it arrives.
      if (oppChar.fallback && tries < 10) retry = setTimeout(() => load(tries + 1), 3000);
    });
    load(0);
    return () => {
      alive = false;
      clearTimeout(retry);
      delete window.JangleAvatars;
    };
  }, [match?.match_id, user?.id, JSON.stringify(user?.avatar3d || null), user?.settings?.crown, !!race3]); // eslint-disable-line react-hooks/exhaustive-deps
  // Player colours (Settings > My colour): the board shows your tiles, base
  // and flag in your pick and the opponent's in theirs (jangle.html
  // JanglePlayerColours sorts out a clash). Bots never pick one.
  const [oppColour, setOppColour] = useState("default");
  useEffect(() => {
    if (!match || !user) return undefined;
    const isP1 = match.player1_id === user.id;
    const oppId = race3 ? race3.a?.id : (isP1 ? match.player2_id : match.player1_id);
    let alive = true;
    (match.is_bot || (race3 && race3.a?.isBot) ? Promise.resolve("default") : colourFor(oppId))
      .then((c) => { if (alive) setOppColour(c); });
    return () => { alive = false; };
  }, [match?.match_id, user?.id, !!race3]); // eslint-disable-line react-hooks/exhaustive-deps
  const myColour = user?.settings?.colour || "default";
  useEffect(() => {
    window.JangleColours = { opp: oppColour, me: myColour };
    sendToIframe({ type: "jangle:colours", opp: oppColour, me: myColour });
  }, [oppColour, myColour]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { delete window.JangleColours; }, []);
  // While the capture animation plays on the board, hold back the result pop-up.
  // The board says when the capture starts (jangle:capture_show, with an
  // upper bound in ms) and when the celebration has played
  // (jangle:capture_done). A capture result can reach this page a moment
  // before the board starts the show, so holdBriefly covers that gap.
  const [captureHold, setCaptureHold] = useState(false);
  const [racePodium, setRacePodium] = useState(null);
  // For the race end pop-up: the podium's winner and rows to go, and the
  // last round the board reported.
  const raceEndRef = useRef({ winner: null, rows: null, round: 0 });
  const captureTimerRef = useRef(null);
  const captureShowingRef = useRef(false);
  const holdFor = useCallback((ms) => {
    setCaptureHold(true);
    clearTimeout(captureTimerRef.current);
    captureTimerRef.current = setTimeout(() => {
      captureShowingRef.current = false;
      setCaptureHold(false);
    }, ms);
  }, []);
  const racePodiumDone = useCallback(() => {
    setRacePodium(null);
    captureShowingRef.current = false;
    clearTimeout(captureTimerRef.current);
    setCaptureHold(false);
  }, []);
  const holdBriefly = useCallback(() => {
    if (!captureShowingRef.current) holdFor(1500);
  }, [holdFor]);
  useEffect(() => {
    const onMsg = (e) => {
      const type = e?.data?.type;
      if (type === "jangle:capture_show") {
        captureShowingRef.current = true;
        markCaptureSeen(matchIdRef.current);
        holdFor(Math.min(Number(e.data.ms) || 0, 30000));
      } else if (type === "jangle:capture_done") {
        captureShowingRef.current = false;
        clearTimeout(captureTimerRef.current);
        setCaptureHold(false);
      } else if (type === "jangle:race_podium") {
        // Raider Races podium (Jack, 2026-10-25): after the winner's cheer
        // on the board; the result pop-up stays held until it's done.
        raceEndRef.current.winner = e.data.winner || null;
        raceEndRef.current.rows = e.data.rows || null;
        setTimeout(() => setRacePodium({ winner: e.data.winner, rows: e.data.rows || {} }), 1500);
      } else if (type === "jangle:race_clock") {
        raceEndRef.current.round = e.data.round || raceEndRef.current.round;
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  // A capture that ended while this player was away (e.g. they lost while
  // signed out) is played once on this device when they come back: once the
  // opponent's tiles are on the board, the board is told the game ended and
  // runs the walk and flag grab, and the result menu waits for it.
  const matchIdRef = useRef(matchId);
  matchIdRef.current = matchId;
  // Every finished turn so far, from the server, as boards in the page's
  // own view (you at the bottom), for the move history under the board.
  const seedMoveHistory = useCallback((w) => {
    api.get(`/match/${matchId}/moves`).then(({ data }) => {
      let rows = 0, info = null;
      try { info = w.JangleBoardInfo?.() || null; rows = info?.rows || 0; } catch { /* ignore */ }
      if (!rows) return;
      // Each turn ends with a Play, which (since 2026-10-15) carries that
      // player's POW cards. Cards only change on their owner's turn, so the
      // other player's count carries over from their last Play.
      const peels = (data?.moves || []).filter((m) => m && m.a === "peel");
      // Nobody has POW cards before their first turn; a Play saved before
      // counts were kept leaves that player's unknown.
      const cards = { H: { p: 0, g: 0 }, A: { p: 0, g: 0 } };
      const steps = buildSteps(fromServerMoves(data?.moves, user?.id), rows, { base: info?.base, cols: info?.cols, wrap: info?.wrap, dropLoose: match?.ui === "stream" })
        .filter((st) => st.kind === "turn")
        .map((st, i) => {
          const t = {};
          Object.keys(st.tiles).forEach((k) => {
            const tile = st.tiles[k];
            t[k] = [tile.color === "me" ? "H" : "A", tile.letter];
          });
          const by = st.actor === "me" ? "H" : "A";
          const pm = peels[i];
          cards[by] = pm && typeof pm.p === "number" ? { p: pm.p, g: Number(pm.g) || 0 } : null;
          const step = { by, t };
          if (cards.H && cards.A) step.c = { p: [cards.H.p, cards.A.p], g: [cards.H.g, cards.A.g] };
          return step;
        });
      if (steps.length) w.postMessage({ type: "jangle:history_seed", steps }, "*");
    }).catch(() => { /* the history just starts from now */ });
  }, [matchId, user?.id]);
  const captureReplayRef = useRef(null);
  // Plays the end of a game that finished while this player was away (a
  // capture walk, or the out-of-turns finale) once the opponent's tiles are
  // on the board. False when it has already been shown on this device.
  const armCaptureReplay = useCallback((id, winnerId, winType = "capture") => {
    if (!id || captureSeen(id)) return false;
    if (captureReplayRef.current?.matchId === id) return true;
    const won = winnerId === user?.id;
    let payload;
    if (winType === "turns" || winType === "sealed" || winType === "timeout") {
      payload = { type: "jangle:turns_over", won, replay: false, sealed: winType === "sealed", timeout: winType === "timeout" };
    }
    else payload = won
      ? { type: "jangle:you_won", reason: "You broke into their base and took the flag!", capture: true }
      : { type: "jangle:opp_win", reason: "Opponent reached your base.", capture: true };
    captureReplayRef.current = { matchId: id, sent: false, payload };
    // Falls back to showing the result if the board can't play it.
    holdFor(8000);
    return true;
  }, [user?.id, holdFor]);
  // Opening a game that is already over. (A page left open while the game
  // ended, e.g. a phone that was put down, hears it from the server's
  // replayed `completed` message instead; see the WebSocket handler.)
  useLayoutEffect(() => {
    if (!match || match.is_bot || match.status !== "completed") return;
    if (!["capture", "turns", "sealed", "timeout"].includes(match.win_type)) return;
    // Watched it end here: it already played. (Keyed by match: this page
    // stays mounted when you go from one game to another.)
    if (sawActiveRef.current === match.match_id) return;
    armCaptureReplay(match.match_id, match.winner_id, match.win_type);
  }, [match?.match_id, match?.status, match?.win_type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Head-to-head intro: once per match on this device, when it's live.
  const [h2hOpen, setH2hOpen] = useState(false);
  useEffect(() => {
    if (!match || match.status !== "active") return;
    // Letterheist has no head-to-head intro.
    if (match.layout === LH_LAYOUT) return;
    const key = `jangle:h2h:${match.match_id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch { /* ignore */ }
    setH2hOpen(true);
  }, [match?.match_id, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  // A player's first game opens with the How to play video, before the
  // head-to-head intro.
  const [guideOpen, setGuideOpen] = useState(false);
  useEffect(() => {
    if (!match || match.status !== "active") return;
    // Letterheist has its own How to play.
    if (match.layout === LH_LAYOUT) return;
    if (shouldShowFirstGameGuide(user)) setGuideOpen(true);
  }, [match?.match_id, match?.status, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // The game iframe holds its opening tile wave and jingle while this is set.
  useEffect(() => {
    window.JangleIntroOpen = h2hOpen || guideOpen;
    return () => { delete window.JangleIntroOpen; };
  }, [h2hOpen, guideOpen]);
  // Raider Races: the server says whether the race is still waiting for
  // racers (resume, race_waiting) or has begun (race_go).
  const introDone = !h2hOpen && !guideOpen;
  const introDoneRef = useRef(introDone);
  introDoneRef.current = introDone;
  const raceWaitRef = useRef(null);
  raceWaitRef.current = (msg) => {
    if (!match || match.layout !== "race") return;
    if (msg.type === "race_go" || (msg.type === "resume" && !msg.race_waiting)) {
      if (!raceWaitingRef.current) return;
      const goAt = msg.go_at_ms || msg.race_go_at_ms;
      const offset = raceClockRef.current ? raceClockRef.current.offset
        : (typeof msg.server_now_ms === "number" ? Date.now() - msg.server_now_ms : 0);
      const localGo = goAt ? goAt + offset : 0;
      if (localGo - Date.now() > 50) {
        setRaceGoAt(localGo);
        return;
      }
      raceStartNow();
      return;
    }
    const ready = msg.race_ready || msg.ready || [];
    const names = [
      [match.player1_id, match.player1_username],
      [match.player2_id, match.player2_username],
      [match.player3_id, match.player3_username],
    ].filter(([id]) => id && id !== user?.id && !ready.includes(id)).map(([, n]) => n || "your opponent");
    raceWaitingRef.current = names;
    setRaceWaiting(names);
    setRaceGoAt(null);
    // This page is past the intro: tell the server (again, after a reconnect).
    if (msg.type === "resume" && introDoneRef.current && !ready.includes(user?.id)) {
      wsSendOrQueue({ action: "race_ready" });
    }
  };
  const raceStartNow = () => {
    if (!raceWaitingRef.current) return;
    raceWaitingRef.current = null;
    setRaceWaiting(null);
    setRaceGoAt(null);
    sendToIframe({ type: "jangle:set_waiting", on: false });
    sendToIframe({ type: "jangle:race_go" });
  };
  // Past the head-to-head intro: ready to race. Said again every second
  // while waiting: a page that missed race_go gets it back as the answer,
  // still inside the count-down. The pings set the clock for the count.
  useEffect(() => {
    if (!raceWaiting) return undefined;
    const tick = () => {
      if (introDoneRef.current) wsSendOrQueue({ action: "race_ready" });
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) {
        try { ws.send(JSON.stringify({ action: "ping", t: Date.now() })); } catch { /* ignore */ }
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    // Still waiting well past the server's one-minute fallback: this
    // socket isn't really hearing the server, so get a fresh one.
    const stale = setTimeout(() => { if (raceWaitingRef.current) reconnectNow(); }, 75000);
    return () => { clearInterval(id); clearTimeout(stale); };
  }, [introDone, !!raceWaiting]); // eslint-disable-line react-hooks/exhaustive-deps
  // The count-down: 3, 2, 1 on the board, then everyone starts together.
  useEffect(() => {
    if (!raceGoAt) return undefined;
    const show = () => {
      const left = raceGoAt - Date.now();
      if (left <= 0) { raceStartNow(); return; }
      sendToIframe({
        type: "jangle:set_waiting", on: true,
        label: `<span style="font-size:34px;line-height:1">${Math.ceil(left / 1000)}</span><br><span style="font-size:10px;opacity:0.7;font-weight:400">get ready to race</span>`,
      });
    };
    show();
    const id = setInterval(show, 100);
    return () => clearInterval(id);
  }, [raceGoAt]); // eslint-disable-line react-hooks/exhaustive-deps
  // The board stays shut until the race begins (re-sent, as a reloaded or
  // just-restored board can open itself again).
  useEffect(() => {
    if (!raceWaiting || raceGoAt) return undefined;
    const shut = () => sendToIframe({
      type: "jangle:set_waiting", on: true,
      label: `Get ready<br><span style="font-size:10px;opacity:0.7;font-weight:400">${
        introDone ? `waiting for ${raceWaiting.join(" and ") || "the race to start"}\u2026` : "the race starts when everyone is ready"}</span>`,
    });
    shut();
    const id = setInterval(shut, 2000);
    return () => clearInterval(id);
  }, [raceWaiting && raceWaiting.join(","), introDone, !!raceGoAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reset all match-scoped React state whenever the matchId changes ──
  // Game.jsx is NOT unmounted on rematch navigation (React Router keeps the
  // component because the path pattern is still `/match/:matchId`). The
  // iframe is rebuilt via its `key` prop, but React state (modal,
  // completion info, move log, peel tracker, round counter) would otherwise
  // carry over from the previous match — making the rematch FEEL like the
  // last game (stale "Defeat" modal hovering over a fresh board, leftover
  // move log, etc).
  const lastMatchIdRef = useRef(matchId);
  useEffect(() => {
    if (lastMatchIdRef.current === matchId) return;
    lastMatchIdRef.current = matchId;
    setGameOverOpen(false);
    setReviewOpen(false);
    setCompletionInfo(null);
    setEloDelta(null);
    setRematchState("idle");
    setDrawOfferFrom(null);
    setRound(1);
    setChatMessages([]);
    setChatUnread(0);
    chatIdsRef.current = new Set();
    completedHandledRef.current = false;
    peelStateRef.current = { me: false, opp: false };
    movesRef.current = [];
  }, [matchId]);

  // Auto-open the game-over modal when landing on (or reconnecting to) an
  // already-completed match. Covers the abandonment scenario where the loser
  // missed the live `completed` WS message because they were disconnected
  // when the auto-forfeit grace expired.
  const completedHandledRef = useRef(false);

  // Opening a finished game (or watching it finish) takes it off Your games.
  const seenPostedRef = useRef(null);
  useEffect(() => {
    const id = match?.match_id;
    if (!id || match.status !== "completed" || seenPostedRef.current === id) return;
    seenPostedRef.current = id;
    api.post(`/matches/${id}/seen`).catch(() => { seenPostedRef.current = null; });
  }, [match?.match_id, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!match || match.status !== "completed") return;
    if (completedHandledRef.current) return;
    if (!user?.id) return;
    // If the live `completed` WS handler already opened the modal with
    // authoritative data, don't clobber it from a stale match fetch.
    if (gameOverOpen || completionInfo) return;
    completedHandledRef.current = true;
    // Derive my result from winner_id (or fallback to winner_color).
    // Jangle has no draw outcome — default to "loss" if we can't be sure
    // who won (rather than ever showing a confusing "Draw" pop-up).
    let result = "loss";
    let myElo, myEloNew, myDelta = null;
    const isP1 = match.player1_id === user.id;
    if (match.win_type === "draw") {
      result = "draw";
    } else if (match.winner_id) {
      result = match.winner_id === user.id ? "win" : "loss";
    } else if (match.winner_color === "A") {
      result = isP1 ? "win" : "loss";
    } else if (match.winner_color === "B") {
      result = isP1 ? "loss" : "win";
    }
    myElo = isP1 ? match.player1_elo : match.player2_elo;
    myEloNew = isP1 ? match.player1_new_elo : match.player2_new_elo;
    if (typeof myEloNew === "number" && typeof myElo === "number") {
      myDelta = myEloNew - myElo;
    } else if (typeof (isP1 ? match.player1_elo_delta : match.player2_elo_delta) === "number") {
      myDelta = isP1 ? match.player1_elo_delta : match.player2_elo_delta;
    }
    setEloDelta(myDelta);
    setCompletionInfo({
      result,
      winReason: formatWinReason(result, match.win_type, match.win_reason, match.layout === "race"),
    });
    setGameOverOpen(true);
  }, [match, user?.id]);

  useEffect(() => {
    if (!match || match.status === "completed") return;
    if (round < TURN_LIMIT - 2 || round > TURN_LIMIT || turnWarnedRef.current === round) return;
    turnWarnedRef.current = round;
    const left = TURN_LIMIT - round;
    toast.info(left === 0
      ? `Turn ${round} of ${TURN_LIMIT}: last turn! Then whoever is closest to the other base wins.`
      : `Turn ${round} of ${TURN_LIMIT}: ${left + 1} turns left each.`);
  }, [round, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Winner celebration (fanfare + streamers). Only for a game this page saw
  // being played, so reopening an old finished match doesn't replay it.
  // The match this page saw being played (live), if any.
  const sawActiveRef = useRef(null);
  const celebratedRef = useRef(null);
  const [celebrate, setCelebrate] = useState(false);
  // The result menu waits while the streamers play.
  const [streamerHold, setStreamerHold] = useState(false);
  useEffect(() => {
    if (match?.status === "active") sawActiveRef.current = match.match_id;
  }, [match?.status]);
  // Layout effect so the menu is held before it can paint.
  useLayoutEffect(() => {
    if (!gameOverOpen || captureHold || completionInfo?.result !== "win") return undefined;
    if (sawActiveRef.current !== matchId || celebratedRef.current === matchId) return undefined;
    celebratedRef.current = matchId;
    setCelebrate(true);
    setStreamerHold(true);
    return undefined;
  }, [gameOverOpen, captureHold, completionInfo?.result, matchId]);
  // A capture walk or out-of-turns finale that you won: start the streamers
  // the moment the board finishes it, rather than after the result has
  // been saved, so there's no pause between the two.
  useEffect(() => {
    let showWinner = null;
    const onMsg = (e) => {
      const type = e?.data?.type;
      if (type === "jangle:capture_show") showWinner = e.data.winner || null;
      else if (type === "jangle:capture_done") {
        const won = showWinner === "H";
        showWinner = null;
        const id = matchIdRef.current;
        if (!won || sawActiveRef.current !== id || celebratedRef.current === id) return;
        celebratedRef.current = id;
        setCelebrate(true);
        setStreamerHold(true);
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);
  useEffect(() => {
    if (!streamerHold) return undefined;
    const id = setTimeout(() => setStreamerHold(false), STREAMERS_BEFORE_MENU_MS);
    return () => clearTimeout(id);
  }, [streamerHold]);
  useEffect(() => {
    if (!celebrate) return undefined;
    const id = setTimeout(() => setCelebrate(false), 4500);
    return () => clearTimeout(id);
  }, [celebrate]);

  // Sync theme to iframe (no reload)
  useEffect(() => {
    const w = iframeRef.current?.contentWindow;
    if (w) w.postMessage({ type: "jangle:theme", theme }, "*");
  }, [theme]);

  // Sync layout to iframe (no reload)
  useEffect(() => {
    const w = iframeRef.current?.contentWindow;
    if (w) w.postMessage({ type: "jangle:layout", layout }, "*");
  }, [layout]);

  // ────────────────── WebSocket relay (human vs human only) ──────────────────
  // Keep the WS open for the entire lifetime of the page on a human match,
  // INCLUDING after match completion — both players need to stay connected
  // through the game-over screen so they can negotiate a rematch.
  const isHumanMatch = match && !match.is_bot;

  const connectWs = useCallback(() => {
    if (!matchId || !isHumanMatch) return;
    const wsBase = API.replace(/^http/, "ws"); // http→ws, https→wss
    // Always include the bearer token as a `?token=` fallback so the WS
    // authenticates correctly when third-party cookies are blocked
    // (incognito, Safari ITP, etc.). Backend prefers cookie when present,
    // falls back to this query param.
    const t = getAuthToken();
    const qs = t ? `?token=${encodeURIComponent(t)}` : "";
    let ws;
    try {
      ws = new WebSocket(`${wsBase}/ws/match/${matchId}${qs}`);
    } catch {
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      setWsLive(true);
      // Flush any queued outbound messages
      const q = outboxRef.current;
      outboxRef.current = [];
      q.forEach((payload) => {
        try { ws.send(JSON.stringify(payload)); } catch { /* ignore */ }
      });
    };
    ws.onclose = (ev) => {
      setWsLive(false);
      // This account signed in on another device: stop, don't reconnect.
      if (ev?.code === WS_CLOSE_SESSION_SUPERSEDED) {
        clearTimeout(reconnectTimer.current);
        signalSessionSuperseded();
        return;
      }
      if (isHumanMatch) {
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connectWs, 1500);
      }
    };
    ws.onerror = () => {/* onclose will fire too */};
    ws.onmessage = (ev) => {
      lastWsMsgAtRef.current = Date.now();
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      // Forward opponent moves into the iframe
      const w = iframeRef.current?.contentWindow;
      if (msg.type === "relay") {
        const myId = user?.id;
        if (msg.from && myId && msg.from === myId) return; // ignore my own echo
        // ── Rematch negotiation relays (no move log) ──
        if (msg.action === "rematch_request") {
          // If we ALSO have a pending rematch out, treat as mutual accept.
          setRematchState((prev) => {
            if (prev === "pending") {
              // Auto-accept — server will create the match and broadcast.
              try {
                wsRef.current?.send(JSON.stringify({
                  action: "relay", relay_action: "rematch_accept", detail: {},
                }));
              } catch { /* ignore */ }
              return "pending";
            }
            return "incoming";
          });
          setGameOverOpen(true);
          return;
        }
        if (msg.action === "rematch_decline") {
          setRematchState("declined");
          toast.info("Opponent declined the rematch");
          // Reset to idle after a few seconds so they can try again
          setTimeout(() => setRematchState((s) => (s === "declined" ? "idle" : s)), 3500);
          return;
        }
        if (msg.action === "rematch_cancel") {
          // Opponent cancelled their pending rematch request to us
          setRematchState((s) => (s === "incoming" ? "idle" : s));
          return;
        }
        // ── Time-control sync relays ──
        if (msg.action === "time_up") {
          // Opponent's clock expired. If we still need to peel, do it on our
          // side too so both clients converge regardless of clock drift.
          // One from a round that has already ended must not auto-play the
          // new one (Jack saw "waiting for opponent" at a round's start).
          if (match?.mode === "realtime" && typeof msg.detail?.round === "number"
              && msg.detail.round !== roundRef.current) return;
          if (match?.mode === "realtime") {
            if (!peelStateRef.current.me) {
              sendToIframe({ type: "jangle:force_peel", round: roundRef.current });
            }
          } else if (match?.mode === "turn_based") {
            // The active player is the one whose timer matters. If THAT'S us,
            // auto-peel ourselves.
            if (activeTurnRef.current === user?.id) {
              sendToIframe({ type: "jangle:force_peel" });
            }
          }
          return;
        }
        // 3 racers: which of the other two this came from ('A' or 'B').
        const who = race3Ref.current?.who[msg.action === "race_bump" ? msg.detail?.by : msg.from];
        // Raider Races: the other player's tile got to that square first.
        if (msg.action === "race_bump") {
          sendToIframe({ type: "jangle:race_bump", ...(msg.detail || {}), ...(who ? { who } : {}) });
          return;
        }
        // Always log opponent moves for the review modal.
        movesRef.current.push({
          actor: "opp", action: msg.action, detail: msg.detail || {},
          ts: Date.now(),
        });
        // Forward to iframe (queue if not ready yet)
        let payload = null;
        if (msg.action === "place") {
          payload = { type: "jangle:opp_place", letter: msg.detail.letter, r: msg.detail.r, c: msg.detail.c, over: !!msg.detail.over };
        } else if (msg.action === "pickup") {
          payload = { type: "jangle:opp_pickup", r: msg.detail.r, c: msg.detail.c };
        } else if (msg.action === "cut") {
          // Opponent cut one of MY tiles. Forward (r,c) from their viewpoint —
          // the iframe will flip the row and remove the H tile + restore the
          // letter to my hand.
          payload = { type: "jangle:opp_cut",
            r: msg.detail.r, c: msg.detail.c,
            letter: msg.detail.letter, cutType: msg.detail.cutType, own: !!msg.detail.own,
            // Sent again by the server: this board still had the tile.
            ...(msg.detail.again ? { again: true } : {}) };
        } else if (msg.action === "peel") {
          playConfirmed(); // their Play follows mine
          payload = { type: "jangle:opp_peel", pink: msg.detail?.pink, green: msg.detail?.green, vowelBack: msg.detail?.vowel_back };
          // Mark opp as peeled. If we already peeled, advance round.
          peelStateRef.current.opp = true;
          // Turn-based: opp ended their turn — flip turn to me + reset clock.
          if (match?.mode === "turn_based") {
            setActiveTurn(user?.id);
            setSecondsLeft(match?.time_control_seconds || 60);
          } else {
            resetTurnTimer();
          }
          // 3 racers: the round moves on when the server says all have played.
          if (peelStateRef.current.me && !race3Ref.current) {
            const next = roundRef.current + 1;
            roundRef.current = next;
            setRound(next);
            peelStateRef.current = { me: false, opp: false };
            // Reset the timer for the new round now so both players see a
            // fresh countdown immediately. The server's round_advance will
            // re-baseline to accurate server-stamped values shortly after;
            // clearing serverTimerRef here lets the fallback useEffect set
            // the full duration in the meantime — and prevents a stale
            // "already at 0" timer from triggering a spurious force_peel
            // at the very start of the incoming round.
            if (match?.mode === "realtime") {
              serverTimerRef.current = null;
              setSecondsLeft(match?.time_control_seconds || 120);
            }
            // Tell iframe the round has advanced so it can unblock our input.
            sendToIframe({ type: "jangle:round_advance", round: next });
          }
        } else if (msg.action === "snapshot") {
          payload = { type: "jangle:opp_snapshot", tiles: msg.detail?.tiles || [] };
          // Their POW card counts, so a reopened game shows them right.
          for (const k of ["pink", "green", "pinkEx"]) if (msg.detail && k in msg.detail) payload[k] = msg.detail[k];
          const replay = captureReplayRef.current;
          if (replay && !replay.sent && replay.matchId === matchId) {
            replay.sent = true;
            // Give the board a moment to draw the tiles and the raiders.
            markCaptureSeen(matchId);
            setTimeout(() => {
              sendToIframe(replay.payload);
              // The board answers with jangle:capture_show; if it can't
              // play it, show the result soon after.
              if (!captureShowingRef.current) holdFor(2500);
            }, 1200);
          }
        } else if (msg.action === "request_snapshot") {
          // Opponent (re)connected and asked us to resend our snapshot so
          // they can rebuild the board on their side. Forward to our iframe
          // which replies with a local_snapshot (caught below).
          sendToIframe({ type: "jangle:send_snapshot_to_opp" });
        } else if (msg.action === "win") {
          // The server forwards the raw relay-win to all peers. If the
          // sender's detail says `resigned_by_me`, THAT player just gave up
          // — meaning *we* won. Inverting the message keeps the iframe and
          // React modal in agreement (otherwise the iframe would render
          // "Opponent reached your base" on the winner's side).
          // 3 racers: a resign doesn't make both others winners; the server
          // picks one and its "completed" ends this board.
          if (race3Ref.current && (msg.detail?.resigned_by_me === true || msg.detail?.turn_limit === true || msg.detail?.won !== true)) {
            payload = null;
          } else if (msg.detail?.resigned_by_me === true) {
            payload = { type: "jangle:you_won", reason: msg.detail?.reason || "Opponent resigned." };
          } else {
            payload = { type: "jangle:opp_win", reason: msg.detail?.reason, capture: !!msg.detail?.capture };
          }
        }
        if (payload && who) payload.who = who;
        if (payload) sendToIframe(payload);
      } else if (msg.type === "completed") {
        playConfirmed();
        // The game is over: no more "<name>'s turn" on the board.
        sendToIframe({ type: "jangle:set_waiting", on: false, turnLock: false });
        // 3 racers: a race another racer ended (they resigned or left) still
        // stops this board and plays the podium (the page ignores a second end).
        const r3 = race3Ref.current;
        if (r3 && !msg.replay && msg.win_type !== "turns") {
          holdBriefly();
          sendToIframe(msg.winner_id === user?.id
            ? { type: "jangle:you_won", reason: msg.win_reason || "You won!" }
            : { type: "jangle:opp_win", who: r3.who[msg.winner_id] || "A", reason: msg.win_reason || "" });
        }
        // Agreed draw: stop the board too (nobody reached a base).
        if (msg.win_type === "draw") {
          setDrawOfferFrom(null);
          sendToIframe({ type: "jangle:draw", reason: "Draw agreed." });
        }
        // Sent again on every reconnect to a finished match; only act on it
        // if this screen hasn't already shown the result.
        if (msg.replay && completedHandledRef.current) return;
        // It ended while this page was away (closed, or a phone put down):
        // play the capture now, once the opponent's tiles arrive.
        if (msg.replay && msg.win_type === "capture") armCaptureReplay(matchId, msg.winner_id, "capture");
        if (msg.win_type === "capture" && !msg.replay) holdBriefly();
        // Out of turns: stop the board too (nobody reached a base, so the
        // board never ended the game itself).
        // The board first shows how close each side got (it holds the
        // result back with jangle:capture_show), except when catching up
        // on a game that ended while this page was away.
        // A sealed-off win (claimed early on proximity) plays the same way,
        // and so does a chess clock running out (closer player wins).
        if (msg.win_type === "turns" || msg.win_type === "sealed" || msg.win_type === "timeout") {
          // A clock running out shows the result straight away (Jack,
          // 2026-11-02): no pause and no closest-to-base replay first.
          if (!msg.replay && msg.win_type !== "timeout") holdBriefly();
          // Away when it ended: the finale plays once the opponent's tiles
          // arrive (unless this device has already shown it).
          if (!(msg.replay && armCaptureReplay(matchId, msg.winner_id, msg.win_type))) {
            sendToIframe({ type: "jangle:turns_over", won: msg.winner_id === user?.id, replay: !!msg.replay,
              who: race3Ref.current?.who[msg.winner_id],
              sealed: msg.win_type === "sealed", timeout: msg.win_type === "timeout" });
          }
        }
        // Server confirms match completion (relay-win path).
        setEloDelta(msg.elo_delta ?? null);
        // Mirror EVERY result field into local match state so the
        // auto-open-on-mount effect (which keys on match.status changing)
        // doesn't fall through to its "loss" default because winner_id is
        // missing. Without this, the secondary effect would clobber the
        // Victory modal we're about to open with a stale "Defeat".
        setMatch((prev) => prev ? {
          ...prev,
          status: "completed",
          layout: msg.layout || prev.layout,
          winner_id: msg.winner_id ?? prev.winner_id,
          win_type: msg.win_type ?? prev.win_type,
          win_reason: msg.win_reason ?? prev.win_reason,
        } : prev);
        // Mark the auto-open path as handled — we're opening the modal
        // here with authoritative data; the secondary effect must not run.
        completedHandledRef.current = true;
        refreshUser?.();
        const delta = msg.elo_delta;
        if (typeof delta === "number") {
          toast.success(`Match recorded! Elo ${delta >= 0 ? "+" : ""}${delta}`);
        }
        // Derive result. `winner_id` is the AUTHORITATIVE source — Elo
        // delta can be 0 for very close-rating matches and would otherwise
        // be misread as a draw. Jangle has no draw outcome — fall back to
        // "loss" rather than ever showing a confusing "Draw" pop-up.
        let result = "loss";
        if (msg.win_type === "draw") {
          result = "draw";
        } else if (msg.winner_id && user?.id) {
          result = msg.winner_id === user.id ? "win" : "loss";
        } else if (typeof delta === "number" && delta > 0) {
          result = "win";
        }
        setCompletionInfo({
          result,
          winReason: formatWinReason(result, msg.win_type, msg.win_reason, (msg.layout || match?.layout) === "race"),
        });
        // Open the game-over screen first; the review modal is one click away.
        setGameOverOpen(true);
      } else if (msg.type === "called_off") {
        // The other player called off a live game that never started.
        toast(`${msg.by || "Your opponent"} called off the game before it started.`);
        navigate("/lobby");
      } else if (msg.type === "opp_left") {
        // Opponent disconnected — we won.
        if (w) w.postMessage({ type: "jangle:you_won", reason: msg.reason || "Opponent left." }, "*");
      } else if (msg.type === "pong") {
        // Race count-down clock: keep the sample with the shortest trip.
        if (typeof msg.t === "number" && typeof msg.server_now_ms === "number") {
          const now = Date.now();
          const rtt = now - msg.t;
          const prev = raceClockRef.current;
          if (rtt >= 0 && (!prev || rtt <= prev.rtt || now - prev.at > 30000)) {
            raceClockRef.current = { offset: Math.round((msg.t + now) / 2) - msg.server_now_ms, rtt, at: now };
          }
        }
      } else if (msg.type === "race_waiting" || msg.type === "race_go") {
        if (msg.type === "race_go" && typeof msg.round_started_at_ms === "number") {
          serverTimerRef.current = {
            startedAtMs: msg.round_started_at_ms,
            durationMs: msg.round_duration_ms,
            serverOffsetMs: raceClockRef.current ? raceClockRef.current.offset
              : Date.now() - (msg.server_now_ms || Date.now()),
          };
        }
        raceWaitRef.current?.(msg);
        if (msg.type === "race_go") {
          const s = computeSecondsLeftFromServer();
          if (s !== null) setSecondsLeft(s);
        }
      } else if (msg.type === "resume" && msg.resync) {
        // The 10-second check (and coming back to the page): only the round,
        // timer and turn. The board stays as it is; putting the saved copy
        // back here could undo a hand just refilled for the new round.
        raceWaitRef.current?.(msg);
        if (Array.isArray(msg.race_theirs) && msg.race_theirs.length) sendToIframe({ type: "jangle:race_theirs", cells: msg.race_theirs });
        try {
          if (msg.clock) adoptClockRef.current(msg.clock);
          if (typeof msg.round === "number" && msg.round > roundRef.current) {
            // A round_advance we never got: start the new round here too.
            roundRef.current = msg.round;
            setRound(msg.round);
            peelStateRef.current = { me: false, opp: false };
            sendToIframe({ type: "jangle:round_advance", round: msg.round });
          }
          if (msg.peeled_this_round) peelStateRef.current = { ...peelStateRef.current, me: true };
          if (typeof msg.round_started_at_ms === "number" && typeof msg.round_duration_ms === "number") {
            serverTimerRef.current = {
              startedAtMs: msg.round_started_at_ms,
              durationMs: msg.round_duration_ms,
              serverOffsetMs: Date.now() - (msg.server_now_ms || Date.now()),
            };
            const s = computeSecondsLeftFromServer();
            if (s !== null) setSecondsLeft(s);
          }
          adoptServerTurnRef.current(msg.active_turn);
        } catch { /* ignore */ }
      } else if (msg.type === "resume") {
        // Refresh-to-resume: rehydrate our own board with the snapshot the
        // server saved for us, and restore the shared round counter.
        raceWaitRef.current?.(msg);
        try {
          if (msg.clock) adoptClockRef.current(msg.clock);
          if (typeof msg.round === "number" && msg.round > 0) setRound(msg.round);
          if (msg.peeled_this_round) {
            peelStateRef.current = { ...peelStateRef.current, me: true };
          }
          // ── Adopt server-authoritative timer baseline ──
          if (typeof msg.round_started_at_ms === "number" && typeof msg.round_duration_ms === "number") {
            serverTimerRef.current = {
              startedAtMs: msg.round_started_at_ms,
              durationMs: msg.round_duration_ms,
              serverOffsetMs: Date.now() - (msg.server_now_ms || Date.now()),
            };
            const s = computeSecondsLeftFromServer();
            if (s !== null) setSecondsLeft(s);
          }
          sendToIframe({
            type: "jangle:resume_me",
            snapshot: msg.snapshot || { tiles: [], frozen: [] },
            peeled: !!msg.peeled_this_round,
          });
          // Raider Races: squares the server gives to someone else (a move
          // missed while away); the board takes its own tiles off them.
          if (Array.isArray(msg.race_theirs) && msg.race_theirs.length) sendToIframe({ type: "jangle:race_theirs", cells: msg.race_theirs });
          // Ask the opponent (if connected) for their tiles too.
          wsSendOrQueue({ action: "relay", relay_action: "request_snapshot", detail: {} });
          // A Play being sent again follows this resume on the new socket:
          // don't hand the turn back to the board in between.
          if (pendingPlayRef.current && msg.active_turn === user?.id) {
            pendingPlayRef.current.serverTurn = msg.active_turn;
          } else {
            if (pendingPlayRef.current) playConfirmed();
            adoptServerTurnRef.current(msg.active_turn);
          }
        } catch { /* ignore */ }
      } else if (msg.type === "chat_history") {
        // Sent on every (re)connect. Only messages newer than what we had
        // count as unread.
        const history = Array.isArray(msg.messages) ? msg.messages : [];
        const known = chatIdsRef.current;
        const fresh = history.filter((m) => !known.has(m.id) && m.from !== user?.id);
        if (!chatOpenRef.current) {
          if (known.size) {
            if (fresh.length) setChatUnread((n) => n + fresh.length);
          } else {
            // First load (e.g. coming back to a turn-based game): count the
            // opponent's messages after the last one this browser showed.
            const lastRead = readChatSeen(matchId);
            const idx = lastRead ? history.findIndex((m) => m.id === lastRead) : -1;
            setChatUnread(history.slice(idx + 1).filter((m) => m.from !== user?.id).length);
          }
        }
        chatIdsRef.current = new Set(history.map((m) => m.id));
        setChatMessages(history);
      } else if (msg.type === "chat") {
        const m = msg.message;
        if (!m || !m.id || chatIdsRef.current.has(m.id)) return;
        chatIdsRef.current.add(m.id);
        setChatMessages((prev) => [...prev, m]);
        if (m.from !== user?.id && !chatOpenRef.current) {
          setChatUnread((n) => n + 1);
        }
      } else if (msg.type === "draw_offer") {
        setDrawOfferFrom(msg.from || null);
        if (msg.closed === "declined" && msg.by !== user?.id) toast.info("Your draw offer was declined.");
        if (msg.closed === "cancelled" && msg.by !== user?.id) toast.info("Your opponent withdrew their draw offer.");
      } else if (msg.type === "chat_error") {
        toast.error(msg.message || "Message not sent");
      } else if (msg.type === "turn_limit") {
        // Both players have had their turns: the board measures who is
        // closer to the other base and we report it.
        sendToIframe({ type: "jangle:turn_limit_check", meIsP1: match?.player1_id === user?.id });
      } else if (msg.type === "play_refused") {
        // The server turned the Play down and said why. "board": this page's
        // board was behind the saved game; the server has sent it back.
        playConfirmed();
        if (msg.reason === "board") {
          toast.error("Your move didn't go through: your board was out of date and has been reloaded. Please play it again.");
        }
        if (msg.active_turn) adoptServerTurnRef.current(msg.active_turn);
        applyTurnGate();
      } else if (msg.type === "turn") {
        if (pendingPlayRef.current) {
          // Still mine: a Play not through yet waits for its retry.
          if (msg.active_turn === user?.id) {
            pendingPlayRef.current.serverTurn = msg.active_turn;
            if (msg.clock) adoptClockRef.current(msg.clock);
            return;
          }
          playConfirmed();
        }
        adoptServerTurnRef.current(msg.active_turn);
        if (msg.clock) adoptClockRef.current(msg.clock);
      } else if (msg.type === "clock") {
        // Both players are in now: the clocks start.
        if (msg.clock) adoptClockRef.current(msg.clock);
      } else if (msg.type === "round_advance") {
        // ── Authoritative round-advance from the server ──
        // The backend has confirmed both players peeled. Use this as the
        // SOURCE OF TRUTH for advancing the round / clearing the waiting
        // banner / resetting the timer, even if our own peelStateRef
        // tracking missed an event (out-of-order WS, force_peel race,
        // mid-flight manual peel API check, etc).
        try {
          const next = typeof msg.round === "number" && msg.round > 0 ? msg.round : roundRef.current + 1;
          // A second round_advance for a round already started here (the
          // local one went first) changes nothing.
          const already = next === roundRef.current;
          roundRef.current = next;
          setRound(next);
          if (!already) peelStateRef.current = { me: false, opp: false };
          // Simultaneous timed games: both clocks start again.
          if (msg.clock) adoptClockRef.current(msg.clock);
          // ── Re-baseline the server-authoritative timer ──
          if (typeof msg.round_started_at_ms === "number" && typeof msg.round_duration_ms === "number") {
            serverTimerRef.current = {
              startedAtMs: msg.round_started_at_ms,
              durationMs: msg.round_duration_ms,
              serverOffsetMs: Date.now() - (msg.server_now_ms || Date.now()),
            };
            const s = computeSecondsLeftFromServer();
            if (s !== null) setSecondsLeft(s);
          }
          if (!already) sendToIframe({ type: "jangle:round_advance", round: next });
        } catch { /* ignore */ }
      } else if (msg.type === "rematch_created") {
        // Server created a fresh match — HARD reload to it so React state,
        // iframe state and any stale game data are fully reset. See bot
        // rematch comment for rationale.
        setRematchState("idle");
        setGameOverOpen(false);
        setReviewOpen(false);
        toast.success("Rematch starting…");
        const newLayout = msg.match?.layout || layout;
        window.location.assign(`/match/${msg.match_id}?layout=${newLayout}`);
      }
    };
  }, [matchId, isHumanMatch, user?.id]);

  connectWsRef.current = connectWs;

  // Heartbeat: a socket that went quiet after a ping is dead even though it
  // says it is open, so get a fresh one (and with it the real turn). Most
  // of all for the player waiting on the other's move, whose page would
  // otherwise never hear about it.
  useEffect(() => {
    if (!isHumanMatch || match?.status !== "active") return undefined;
    let pingAt = 0;
    const id = setInterval(() => {
      if (document.visibilityState === "hidden") { pingAt = 0; return; }
      const ws = wsRef.current;
      if (!ws || ws.readyState !== 1) { pingAt = 0; return; }
      if (pingAt && lastWsMsgAtRef.current < pingAt && Date.now() - pingAt > 8000) {
        pingAt = 0;
        reconnectNow();
        return;
      }
      if (!pingAt || lastWsMsgAtRef.current >= pingAt) {
        pingAt = Date.now();
        try { ws.send(JSON.stringify({ action: "ping" })); } catch { /* ignore */ }
      }
    }, 10000);
    return () => clearInterval(id);
  }, [isHumanMatch, match?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isHumanMatch) return;
    connectWs();
    return () => {
      playConfirmed();
      clearTimeout(reconnectTimer.current);
      // Leaving the page: close without the auto-reconnect in onclose.
      if (wsRef.current) wsRef.current.onclose = null;
      try { wsRef.current?.close(); } catch {/* noop */}
      wsRef.current = null;
      setWsLive(false);
    };
  }, [connectWs, isHumanMatch]);

  // Bridge: listen for iframe → relay outbound to server
  useEffect(() => {
    function onMessage(ev) {
      const d = ev.data;
      if (!d || typeof d !== "object") return;
      if (d.type === "jangle:page_ready") {
        if (typeof d.rows === "number" && d.rows > 0) boardRowsRef.current = d.rows;
        const w = iframeRef.current?.contentWindow;
        if (w && ev.source === w) bootIframeRef.current(w);
        return;
      }
      // Iframe-initiated lobby navigation (from victory/defeat modal)
      if (d.type === "jangle:return_to_lobby") {
        navigate("/lobby");
        return;
      }
      // Score reporting via API on local win (also handles bot mode)
      // The leader claims the win because the opponent is sealed off.
      if (d.type === "jangle:sealed_claim") {
        if (isHumanMatch) {
          wsSendOrQueue({ action: "relay", relay_action: "win", detail: { sealed: true, won: true } });
        }
        return;
      }
      if (d.type === "jangle:turn_limit_result") {
        if (isHumanMatch) {
          wsSendOrQueue({
            action: "relay", relay_action: "win",
            detail: { turn_limit: true, won: !!d.won, reason: d.reason || "" },
          });
        }
        return;
      }
      if (d.type === "jangle:local_win") {
        const won = !!d.won;
        if (isHumanMatch) {
          // The backend's _persist_relay_win is the ONLY writer for human
          // matches — do NOT also POST /complete.
          wsSendOrQueue({
            action: "relay", relay_action: "win",
            detail: { reason: d.reason, won, capture: !!d.capture },
          });
          return;
        }
        // Bot mode: persist via REST as before
        if (!submitting && match?.status !== "completed") {
          submitResult(won ? "win" : "loss", !!d.capture, d.sealed ? "sealed" : d.turns ? "turns" : "");
        }
        return;
      }
      // Bot matches: every turn's board and hand, sent just before the result.
      if (!isHumanMatch && d.type === "jangle:bot_record") {
        botRecordRef.current = d.record || null;
        return;
      }
      // Same time against the bot with chess clocks: the page runs both
      // clocks and tells us what is left, for the clock pills.
      if (!isHumanMatch && d.type === "jangle:bot_clock") {
        if (d.ms && match) {
          const botId = match.player1_id === user?.id ? match.player2_id : match.player1_id;
          const ms = { [user?.id]: d.ms.H, [botId]: d.ms.A };
          const ticking = [d.ticking?.H && user?.id, d.ticking?.A && botId].filter(Boolean);
          setClock({ ms, ticking, running: ticking.length > 0, at: Date.now() });
        }
        return;
      }
      // Bot matches: the page sends the whole game after every bot turn;
      // keep it with the match so the player can leave and come back.
      if (!isHumanMatch && d.type === "jangle:bot_save") {
        if (match?.status === "active" && d.state) {
          api.put(`/match/${matchId}/bot-state`, { state: d.state, round: d.round ?? null })
            .catch(() => { /* the next bot turn saves again */ });
        }
        return;
      }
      // Bot matches: increment the shared round badge locally each time
      // the player peels (no opponent to wait for).
      if (!isHumanMatch && d.type === "jangle:local_peel") {
        setRound((r) => r + 1);
        resetTurnTimer();
        return;
      }
      // Relay user actions to opponent
      if (!isHumanMatch) return;
      if (d.type === "jangle:local_place") {
        movesRef.current.push({ actor: "me", action: "place", detail: { letter: d.letter, r: d.r, c: d.c }, ts: Date.now() });
        wsSendOrQueue({ action: "relay", relay_action: "place",
          // over: a Raider Races tile laid over the rival's unplayed non-word tile.
          detail: { letter: d.letter, r: d.r, c: d.c, ...(d.over ? { over: true } : {}), ...(boardRowsRef.current ? { rows: boardRowsRef.current } : {}) } });
      } else if (d.type === "jangle:local_pickup") {
        movesRef.current.push({ actor: "me", action: "pickup", detail: { r: d.r, c: d.c }, ts: Date.now() });
        wsSendOrQueue({ action: "relay", relay_action: "pickup",
          detail: { r: d.r, c: d.c, ...(boardRowsRef.current ? { rows: boardRowsRef.current } : {}) } });
      } else if (d.type === "jangle:local_cut") {
        // Cut-through: I removed an opponent tile at MY (r,c). Relay to
        // opponent so they can remove the matching H tile from THEIR side
        // and return that letter to their hand. The iframe handles the
        // coordinate flip on the receiver side.
        movesRef.current.push({ actor: "me", action: "cut",
          detail: { r: d.r, c: d.c, vr: d.vr, letter: d.letter, cutType: d.cutType, own: !!d.own }, ts: Date.now() });
        wsSendOrQueue({ action: "relay", relay_action: "cut",
          detail: { r: d.r, c: d.c, vr: d.vr, letter: d.letter, cutType: d.cutType, own: !!d.own, ...(boardRowsRef.current ? { rows: boardRowsRef.current } : {}) } });
      } else if (d.type === "jangle:need_resume") {
        // The board never got its saved game back: ask the server for it
        // again. A page that can ask has loaded, so hand over anything still
        // held back for it.
        const w = iframeRef.current?.contentWindow;
        if (w && ev.source === w && !iframeReadyRef.current) {
          iframeReadyRef.current = true;
          const q = iframeInboxRef.current;
          iframeInboxRef.current = [];
          q.forEach((payload) => { try { w.postMessage(payload, "*"); } catch { /* ignore */ } });
        }
        if (isHumanMatch) wsSendOrQueue({ action: "request_resync" });
      } else if (d.type === "jangle:need_opp_snapshot") {
        // The board is missing tiles from the opponent's last turn: ask for
        // their saved board again (the same as reopening the game does).
        if (isHumanMatch) wsSendOrQueue({ action: "relay", relay_action: "request_snapshot", detail: {} });
      } else if (d.type === "jangle:local_snapshot") {
        // Periodic state-snapshot for drift correction. NOT logged as a move.
        // hand/bag/pink/green/pinkEx/awards/gems/turn/handOrder are kept by the server for
        // resuming and never relayed to the opponent.
        const detail = { tiles: d.tiles || [], frozen: d.frozen || [] };
        if (boardRowsRef.current) detail.rows = boardRowsRef.current;
        ["hand", "bag", "pink", "green", "pinkEx", "awards", "gems", "powsq", "turn", "handOrder"].forEach((k) => {
          if (d[k] !== undefined) detail[k] = d[k];
        });
        wsSendOrQueue({ action: "relay", relay_action: "snapshot", detail });
      } else if (d.type === "jangle:local_peel") {
        // The round it belongs to: the server ignores a Play that reaches it
        // after that round has already ended on the clock. The board tags
        // each Play with the round it was pressed in, since checking the
        // words can take a moment and the round may end meanwhile.
        const playRound = typeof d.round === "number" && d.round > 0 ? d.round : roundRef.current;
        // A Play from a round that has ended: those tiles were played with
        // that round, and the new round is still this player's to play.
        if (match?.mode === "realtime" && (d.stale || playRound !== roundRef.current)) return;
        movesRef.current.push({ actor: "me", action: "peel", detail: {}, ts: Date.now() });
        // My POW cards at the end of the turn, kept with it on the server
        // for the move history (and shown to the opponent as they arrive).
        const peelDetail = { round: playRound };
        if (typeof d.pink === "number") peelDetail.pink = d.pink;
        if (typeof d.green === "number") peelDetail.green = d.green;
        // Vowel Prowl: the cut vowels this Play gave back (game_ws _settle_vowel_cuts).
        if (Array.isArray(d.vowelBack)) peelDetail.vowel_back = d.vowelBack;
        const peelMsg = { action: "relay", relay_action: "peel", detail: peelDetail };
        wsSendOrQueue(peelMsg);
        // Turn-based against a person: watch for the server's "turn" saying
        // it passed, and send the Play again on a fresh socket if not.
        if (match?.mode === "turn_based") {
          playConfirmed();
          pendingPlayRef.current = { payload: peelMsg, tries: 0, timer: null, serverTurn: null };
          armPlayCheck();
        }
        // Mark me as peeled locally. If opp already peeled, advance round.
        peelStateRef.current.me = true;
        // Turn-based: my turn ended — flip turn to opp, reset clock.
        if (match?.mode === "turn_based") {
          const oppUid = match.player1_id === user?.id ? match.player2_id : match.player1_id;
          setActiveTurn(oppUid);
          setSecondsLeft(match?.time_control_seconds || 60);
        } else {
          resetTurnTimer();
        }
        if (peelStateRef.current.opp && !race3Ref.current) {
          const next = roundRef.current + 1;
          roundRef.current = next;
          setRound(next);
          peelStateRef.current = { me: false, opp: false };
          // Same timer reset as the opp-peel path above — clear stale server
          // stamps so the new round starts with a fresh full-duration countdown.
          if (match?.mode === "realtime") {
            serverTimerRef.current = null;
            setSecondsLeft(match?.time_control_seconds || 120);
          }
          // Tell our iframe to unblock too — opp was already waiting.
          sendToIframe({ type: "jangle:round_advance", round: next });
        }
      } else if (d.type === "jangle:scroll_base_above_dock") {
        // Mobile keyboard dock opened in the iframe — scroll the React
        // page so the player's base row sits just above the dock. The
        // iframe asks us to scroll because its content fits its own
        // viewport and only the parent document scrolls on mobile.
        try {
          const iframeEl = iframeRef.current;
          if (!iframeEl) return;
          const w = iframeEl.contentWindow;
          if (!w) return;
          const baseR = 19;
          const baseCell = w.document.querySelector(`.cell[data-r="${baseR}"]`);
          if (!baseCell) return;
          const cellRect = baseCell.getBoundingClientRect();
          const iframeRect = iframeEl.getBoundingClientRect();
          // base cell bottom in page-document coordinates
          const cellBottomInPage = iframeRect.top + cellRect.bottom + window.scrollY;
          const desiredBottom = window.innerHeight - (d.dockH || 170) - 10;
          const targetScrollY = cellBottomInPage - desiredBottom;
          if (Math.abs(targetScrollY - window.scrollY) < 2) return;
          window.scrollTo({ top: Math.max(0, targetScrollY), left: 0, behavior: "smooth" });
        } catch { /* ignore */ }
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [isHumanMatch, submitting, match?.status, sendToIframe, wsSendOrQueue]); // eslint-disable-line react-hooks/exhaustive-deps

  // A game page has loaded and listens: hand over the saved board, opponent
  // tiles and anything else held back for it, then ask for the opponent's
  // board. Runs once per page load, on jangle:page_ready from the page, or
  // 300 ms after the iframe's load event if the page never says so (it used
  // to always wait those 300 ms).
  bootIframeRef.current = (w) => {
    if (!w || !match) return;
    let doc = null;
    try { doc = w.document; } catch { /* ignore */ }
    if (doc && bootDocRef.current === doc) return;
    bootDocRef.current = doc;
    clearTimeout(bootTimerRef.current);
    iframeReadyRef.current = true;
    const q = iframeInboxRef.current;
    iframeInboxRef.current = [];
    q.forEach((payload) => {
      try { w.postMessage(payload, "*"); } catch { /* ignore */ }
    });
    // Refresh-resume: ask the opponent (if any) to resend their
    // tile snapshot so the rejoining player can see the current
    // board state. Harmless on a fresh load — opponent will just
    // resend their (empty) snapshot.
    if (!match.is_bot) {
      wsSendOrQueue({
        action: "relay", relay_action: "request_snapshot", detail: {},
      });
    }
    // A reloaded iframe starts unlocked; restore the turn gate.
    applyTurnGate(true);
    // The back/forward buttons under the board: hand the page every
    // turn played so far (bot games keep their own with the game).
    if (!match.is_bot) seedMoveHistory(w);
    try { w.postMessage({ type: "jangle:round", round: roundRef.current }, "*"); } catch { /* ignore */ }
  };

  if (loading || !match) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background text-muted-foreground" data-testid="game-loading">
        Loading match…
      </div>
    );
  }

  const isP1 = match.player1_id === user?.id;
  // Each person's Elo now (player*_live_elo), the same number as the
  // leaderboard and profiles; player*_elo is only the Elo going into this
  // game. Bots have no live Elo and keep their label.
  const eloOf = (n) => match[`player${n}_live_elo`] ?? match[`player${n}_elo`];
  const me = {
    id: user?.id,
    username: isP1 ? match.player1_username : match.player2_username,
    elo: eloOf(isP1 ? 1 : 2),
  };
  const opp = {
    id: isP1 ? match.player2_id : match.player1_id,
    username: isP1 ? match.player2_username : match.player1_username,
    elo: eloOf(isP1 ? 2 : 1),
    isBot: match.is_bot,
  };
  // The board's own name labels get the Elo from when the board first
  // loaded: they ride in the iframe src, and changing src reloads the game.
  if (!iframeEloRef.current) iframeEloRef.current = { me: me.elo, opp: opp.elo };

  const completed = match.status === "completed";
  const isLH = layout === LH_LAYOUT;
  // A game against a person in the Stream look (Jack, 2026-11-04).
  const isStream = match.ui === "stream" && !isLH && layout !== "race";
  // Your character in the game-over pop-up: your Letterheist piece there.
  const meCharacter = !user ? null
    : isLH ? { look: LH_ME_LOOK }
    : myCharacter(user);

  const submitResult = async (result, capture = false, winType = "") => {
    // Jangle has no draw outcome. Coerce any legacy "draw" caller to "win"
    // so the user — who reached this dropdown to record their bot result —
    // gets the Victory pop-up they expect rather than a confusing Draw.
    if (result === "draw") result = "win";
    setSubmitting(true);
    // Bot games show the result at once, then record it (Jack, 2026-11-02:
    // waiting on the server felt laggy). Bot games don't change Elo, so the
    // pop-up has nothing to wait for.
    if (match.is_bot) {
      setCompletionInfo({ result, winReason: formatWinReason(result, winType, "", layout === "race") });
      setGameOverOpen(true);
    }
    try {
      const { data } = await api.post(`/match/${matchId}/complete`, {
        result, layout, capture: result === "win" && capture,
        ...(match.is_bot && botRecordRef.current ? { record: botRecordRef.current } : {}),
      });
      // The finished match only carries Elo going in and after this game;
      // keep the header on each person's Elo now (after this game).
      setMatch((prev) => ({
        ...data.match,
        player1_live_elo: data.match?.player1_new_elo ?? prev?.player1_live_elo,
        player2_live_elo: data.match?.player2_new_elo ?? prev?.player2_live_elo,
      }));
      setEloDelta(data.elo_delta);
      await refreshUser();
      toast.success(match.is_bot
        ? "Game recorded. Bot games don't change your Elo."
        : `Match recorded! Elo ${data.elo_delta >= 0 ? "+" : ""}${data.elo_delta}`);
      if (!match.is_bot) {
        setCompletionInfo({ result, winReason: formatWinReason(result, winType, "", layout === "race") });
        setGameOverOpen(true);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to record");
    } finally {
      setSubmitting(false);
    }
  };

  // Rematch — for HUMAN matches: send a request via WS relay, opponent must
  // accept. The backend creates the new match on accept and broadcasts a
  // `rematch_created` message that navigates both clients.
  // For BOT matches: instantly create a new bot match against the same
  // difficulty (no opponent to negotiate with).
  const rematch = async () => {
    try {
      if (match.is_bot) {
        setGameOverOpen(false);
        setReviewOpen(false);
        const { data } = await api.post("/match/find", {
          mode: match.mode,
          opponent: "ai",
          layout: layout === "boardgame" ? "boardgame2" : layout === "infinity" ? "infinity2" : layout === "boardgame4" ? "firefly2" : layout,
          bot_elo: match.player2_elo,
          bot_name: match.player2_username,
          time_control_seconds: match.time_control_seconds,
          // A rematch keeps any test rules the game was played under.
          ...(match.test_rules ? { test_rules: match.test_rules } : {}),
          ...(match.same_tiles ? { same_tiles: true } : {}),
          ...(match.simultaneous ? { simultaneous: true } : {}),
          ...(match.simultaneous && match.clock ? { clock: match.clock } : {}),
          // ...and who starts: You, the bot or Random, as picked.
          ...(match.first_choice ? { first: match.first_choice } : {}),
          // A Stream bot game's rematch stays in Stream (Jack, 2026-11-04).
          ...(match.ui === "stream" ? { ui: "stream" } : {}),
          // Always start a brand-new bot match for the Rematch button —
          // never resurrect an unrelated "active" match the user may have.
          force_new: true,
        });
        if (data.status === "matched" && data.match_id && data.match_id !== matchId) {
          // HARD reload — guarantees a brand-new game state. React Router's
          // soft `navigate()` keeps Game.jsx mounted across `/match/:id`
          // paths, and despite state resets the user has reported stale
          // boards. `window.location.assign` forces a full page load:
          // fresh React tree, fresh iframe, fresh everything.
          window.location.assign(`/match/${data.match_id}?layout=${layout === "boardgame" ? "boardgame2" : layout === "infinity" ? "infinity2" : layout === "boardgame4" ? "firefly2" : layout}`);
        } else {
          // Defensive: if for any reason the backend returned the SAME match
          // id (shouldn't happen with force_new) or an unexpected payload,
          // let the user know rather than silently leaving them on the
          // completed match.
          toast.error("Could not start rematch — try New Game from the lobby.");
          setGameOverOpen(true);
        }
        return;
      }
      // Human match: send a rematch request to the opponent via the
      // still-open WebSocket. Don't close the modal — show "Waiting…".
      setRematchState("pending");
      wsSendOrQueue({
        action: "relay", relay_action: "rematch_request", detail: {},
      });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to start rematch");
      setRematchState("idle");
    }
  };

  const rematchAccept = () => {
    // Tell the server we accept — it will create the match and broadcast
    // `rematch_created` to both players via the WS, which navigates us.
    wsSendOrQueue({ action: "relay", relay_action: "rematch_accept", detail: {} });
  };

  const rematchDecline = () => {
    wsSendOrQueue({ action: "relay", relay_action: "rematch_decline", detail: {} });
    setRematchState("idle");
  };

  const rematchCancel = () => {
    wsSendOrQueue({ action: "relay", relay_action: "rematch_cancel", detail: {} });
    setRematchState("idle");
  };

  // ── Resign flow ──
  // Performs the actual resign: WS relay to notify opponent immediately
  // (server credits them the win), plus a REST `/complete` call as a
  // belt-and-braces fallback. Then locally shows the game-over modal.
  const performResign = async (navigateAfter = false) => {
    setResignModal({ open: false, exiting: false });
    completedHandledRef.current = true;
    // ── REST `/resign` is the PRIMARY path ──
    // The WS relay below is a nice-to-have for instant in-game opponent
    // notification, but if the WS is dead (mobile background, screen
    // lock, long absence) the relay silently drops. The REST call
    // always reaches the server, credits the opponent the win, and the
    // server itself pushes `opp_left` to the opponent's WS if they're
    // connected. This guarantees the opponent's "Defeat" modal fires.
    try {
      await api.post(`/match/${matchId}/resign`);
    } catch (e) {
      // Last-ditch fallback: try /complete in case /resign fails for
      // some reason. /resign should be idempotent for completed matches.
      try { await api.post(`/match/${matchId}/complete`, { result: "loss" }); }
      catch { /* server may have already completed it */ }
    }
    // Best-effort WS relay (instant push to opponent if they're online).
    try {
      wsSendOrQueue({
        action: "relay", relay_action: "win",
        detail: { won: false, resigned_by_me: true, reason: "Opponent resigned." },
      });
    } catch { /* ignore */ }
    // ── Pre-acknowledge the loss so the Lobby's "Defeat" notification
    //    modal never re-shows it. The player consciously chose to resign;
    //    surfacing another pop-up after they've already exited is jarring.
    try {
      const seenRaw = localStorage.getItem("jangle:defeats_seen") || "[]";
      const seen = JSON.parse(seenRaw);
      if (!seen.includes(matchId)) {
        seen.push(matchId);
        localStorage.setItem("jangle:defeats_seen", JSON.stringify(seen.slice(-50)));
      }
    } catch { /* ignore */ }
    if (navigateAfter) {
      navigate("/lobby");
    } else {
      // Raider Races: the podium plays first, and holds the pop-up.
      if (match?.layout === "race") { holdBriefly(); sendToIframe({ type: "jangle:race_resigned" }); }
      setCompletionInfo({ result: "loss", winReason: "You resigned." });
      setGameOverOpen(true);
    }
  };

  performResignRef.current = performResign;

  const newGame = () => {
    setGameOverOpen(false);
    setReviewOpen(false);
    // After a bot game, pick the next bot yourself (Jack, 2026-10-12)
    // instead of searching for a person.
    navigate(match?.is_bot ? "/lobby?pick=bot" : "/lobby?autoFind=1");
  };

  // Raider Races against people end on the race pop-up and a result pill in
  // the header. Without a podium (a resign), the winner follows the result.
  const raceVsPeople = match.layout === "race" && !match.is_bot;
  const raceEnd = raceEndRef.current;
  const raceWon = completionInfo?.result === "win";
  const raceWinner = raceEnd.winner || (raceWon ? "H" : "A");
  const raceRacers = raceVsPeople ? [
    { name: user?.username || "You", you: true, character: user ? myCharacter(user) : null, rows: raceEnd.rows?.H, won: raceWon },
    { name: (race3 ? race3.a.username : match.player1_id === user?.id ? match.player2_username : match.player1_username) || "Opponent",
      character: oppCharacter, rows: raceEnd.rows?.A, won: !raceWon && raceWinner === "A" },
    ...(race3 ? [{ name: race3.b.username || "Opponent", character: opp2Character, rows: raceEnd.rows?.B, won: !raceWon && raceWinner === "B" }] : []),
  ] : [];
  const raceOver = raceVsPeople && !!completionInfo && !captureHold && !streamerHold;
  const racePlace = ["1st", "2nd", "3rd"][racePlaces(raceRacers).findIndex((r) => r.you)] || "2nd";

  const mainMenu = () => {
    setGameOverOpen(false);
    setReviewOpen(false);
    navigate("/lobby");
  };

  return (
    <div
      className="fixed inset-0 flex flex-col bg-background text-foreground"
      style={{ ...(keyboardBox ? { top: keyboardBox.top, height: keyboardBox.height, bottom: "auto" } : {}), ...(isLH ? { background: LH_PAGE_BG } : {}) }}
      data-testid="game-page"
    >
      <FirstGameGuide user={user} open={guideOpen} onClose={() => setGuideOpen(false)} />
      {h2hOpen && !guideOpen && <HeadToHead user={user} me={me} opp={opp} onDone={() => setH2hOpen(false)} sound={isStream}
        colours={resolveColours(myColour, oppColour)}
        crownCategory={match.layout === "race" ? "race" : isLH ? null : match.clock ? ["jangle", "live"] : "jangle"} />}
      {/* Letterheist games get Letterheist's own header, Settings and How to
          play (Jack, 2026-10-25: "Letterheist screen for all"). */}
      {isLH && (
        <>
          <LhHeader
            onBack={() => navigate(user?.letterheist_test ? "/letterheist" : "/lobby")}
            subtitle={`vs ${opp.username || "Opponent"}`}
            onSettings={() => setSettingsOpen(true)}
            extra={!match.is_bot && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setChatOpen((o) => !o); setChatUnread(0); }}
                className="relative text-white hover:bg-white/10"
                aria-label={chatUnread ? `Chat, ${chatUnread} unread` : "Chat"}
                data-testid="game-chat-btn"
              >
                <ChatCircle size={22} weight="fill" />
                {chatUnread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-[18px] text-center"
                    data-testid="chat-unread-badge">
                    {chatUnread > 9 ? "9+" : chatUnread}
                  </span>
                )}
              </Button>
            )}
            play={match.status === "active" ? {
              can: !completed && canPlay,
              onClick: () => { try { iframeRef.current?.contentWindow?.document.getElementById("btn-peel")?.click(); } catch { /* ignore */ } },
            } : null}
          />
          <LhSettingsDialog
            open={settingsOpen} onOpenChange={setSettingsOpen}
            muted={lhSound.muted} onToggleSound={lhSound.toggleSound}
          >
            {match.status === "active" && (
              <Button variant="outline" className="w-full h-12 text-lg font-bold" style={{ color: "#b42318", borderColor: "#e3b4ae", background: "#ffffff" }}
                onClick={() => { setSettingsOpen(false); setResignModal({ open: true, exiting: false }); }}
                data-testid="lh-resign">
                <Flag size={18} weight="fill" className="mr-2" /> Resign this game
              </Button>
            )}
          </LhSettingsDialog>
          <LhHelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
        </>
      )}
      {/* Slim top bar */}
      <header className={`flex-none h-[4.5rem] px-4 md:px-6 ${headerCollapsed || isLH || isStream ? "hidden" : "flex"} items-center justify-between border-b border-border bg-surface/80 backdrop-blur-xl z-10`}>
        <div className="flex items-center gap-3 min-w-0">
          {/* No Exit while a timed game is live: the only ways out are
              resigning or the game ending. Untimed turn-based games against
              people, and bot games (saved after every bot turn), can be left
              and picked up later from "Your games". */}
          {(match.status !== "active" || noTimeLimit || match.is_bot) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/lobby")}
              className="rounded-full -ml-2 text-muted-foreground hover:text-foreground"
              data-testid="game-back-btn"
            >
              <ArrowLeft size={16} className="mr-1" /> {match.status === "active" ? "Games" : "Lobby"}
            </Button>
          )}
          {/* WS connection indicator — visible only on desktop. On mobile
              the header is already tight; players don't need a Live pill
              cluttering the bar (the "Connecting…" / disconnect toast still
              fires if the WS actually drops). */}
          {!match.is_bot && (
            <span
              className={`hidden md:inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border
                ${wsLive ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/30"
                         : "bg-amber-500/15 text-amber-500 border-amber-500/30"}`}
              data-testid="game-ws-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${wsLive ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`} />
              {wsLive ? "Live" : "Connecting…"}
            </span>
          )}
          <JangleBrand className="hidden md:inline-flex" logoClassName="h-8" />
        </div>

        {/* You vs opponent: picture with name underneath; tap either to enlarge.
            Sits at the left on phones and in the middle on desktop. */}
        <div className="flex flex-1 items-center justify-start md:justify-center gap-2 md:gap-4 min-w-0 px-1 md:px-2" data-testid="game-players">
          <PlayerChip
            userId={me.id}
            username={me.username}
            version={user?.avatar_v ?? null}
            elo={me.elo}
            label="You"
            fallbackClassName="bg-brand text-brand-foreground"
            testId="game-player-me"
          />
          {isClock && <ClockPill ms={clockLeft(me.id)} live={clockTicking(me.id)} testId="game-clock-me" />}
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">vs</span>
          {isClock && <ClockPill ms={clockLeft(opp.id)} live={clockTicking(opp.id)} testId="game-clock-opp" />}
          <PlayerChip
            userId={opp.id}
            username={opp.username}
            elo={opp.elo}
            isBot={opp.isBot}
            label="Opponent"
            canViewProfile={match.status !== "active" || noTimeLimit}
            fallbackClassName="bg-[hsl(var(--chart-3))] text-background"
            testId="game-player-opp"
          />
        </div>

        <div className="flex items-center gap-1">
          {/* Game clock */}
          {match.status === "active" && !match.is_bot && !isClock && (() => {
            const mm = Math.floor(secondsLeft / 60);
            const ss = secondsLeft % 60;
            const lowTime = !noTimeLimit && secondsLeft <= 10;
            const expired = !noTimeLimit && secondsLeft === 0;
            const tcLabel = match.mode === "realtime" ? "Round" : "Turn";
            // On phones a turn-based game with no time limit doesn't need the "Turn: No limit" pill.
            const turnBasedPhoneHide = match.mode === "turn_based" && noTimeLimit;
            return (
              <span
                className={`${turnBasedPhoneHide ? "hidden md:inline-flex" : "inline-flex"} items-center gap-1.5 text-[10px] md:text-[11px] uppercase tracking-[0.15em] md:tracking-[0.18em] font-bold mr-1 md:mr-2 px-2 md:px-2.5 py-1 rounded-full border tabular-nums transition-colors ${
                  expired
                    ? "bg-destructive/15 text-destructive border-destructive/40 animate-pulse"
                    : lowTime
                    ? "bg-destructive/10 text-destructive border-destructive/30 animate-pulse"
                    : "bg-surface text-foreground border-border"
                }`}
                data-testid="game-clock"
                title={noTimeLimit ? `${tcLabel} time control: none` : `${tcLabel} time control: ${match.time_control_seconds || 0}s`}
              >
                <span className="opacity-60 text-[9px]">{tcLabel}</span>
                <span className="font-mono">{noTimeLimit ? "No limit" : `${mm}:${String(ss).padStart(2, "0")}`}</span>
              </span>
            );
          })()}

          {/* Shared round counter for real-time games. Turn-based games show
              "Turn N of 15" beside the arrows under the board instead. */}
          {raceOver && (
            <span className="mr-1 md:mr-2"><RaceResultPill won={raceWon} place={racePlace} onClick={() => setGameOverOpen(true)} /></span>
          )}
          {match.mode !== "turn_based" && !(raceVsPeople && completionInfo) && <span
            // Simultaneous games on phones: the two clocks fill the header,
            // and "Turn N of 15" under the board says the same.
            className={`${isSimul ? "hidden md:inline-flex" : "inline-flex"} items-center gap-1 text-[9px] md:text-[10px] uppercase tracking-[0.18em] md:tracking-[0.2em] font-bold mr-1 md:mr-2 px-1.5 md:px-2 py-0.5 rounded-full bg-brand/10 text-brand border border-brand/20`}
            data-testid="round-badge"
            title="Round counter"
          >
            Round {round}/{TURN_LIMIT}
          </span>}
          <span className="hidden lg:inline text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mr-2">
            {match.mode === "turn_based" ? "Turn-based" : isSimul ? "Same time" : "Real-time"}
          </span>

          {/* Layout picker (hidden on phones to keep the header tidy) */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="hidden md:inline-flex rounded-full gap-1.5 text-muted-foreground hover:text-foreground"
                disabled={completed}
                data-testid="game-layout-menu"
              >
                <Layout size={16} />
                <span className="hidden sm:inline capitalize text-sm">{LAYOUTS.find((l) => l.id === layout)?.name || layout.replace("_", " ")}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Board layout
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LAYOUTS.filter((l) => l.id !== "boardgame" && l.id !== "race").map((l) => (
                <DropdownMenuItem
                  key={l.id}
                  onClick={() => setLayout(l.id)}
                  data-testid={`game-layout-${l.id}`}
                  className={layout === l.id ? "text-brand font-semibold" : ""}
                >
                  {l.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {!match.is_bot && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setChatOpen((o) => !o); setChatUnread(0); }}
              className="rounded-full relative [&_svg]:size-[26px]"
              aria-label={chatUnread ? `Chat, ${chatUnread} unread` : "Chat"}
              data-testid="game-chat-btn"
            >
              <ChatCircle size={26} weight="fill" className="text-foreground dark:text-white" />
              {chatUnread > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-[18px] text-center"
                  data-testid="chat-unread-badge"
                >
                  {chatUnread > 9 ? "9+" : chatUnread}
                </span>
              )}
            </Button>
          )}

          {/* Play: the same as the page's own Play button (it clicks it, so
              the phone confirm still shows). Greyed when it isn't your turn. */}
          {match.status === "active" && !raceOver && (
            <Button
              size="sm"
              className="rounded-full bg-[#0288d1] hover:bg-[#039be5] text-white gap-1.5 ml-1"
              disabled={completed || !canPlay}
              onClick={() => {
                try { iframeRef.current?.contentWindow?.document.getElementById("btn-peel")?.click(); } catch {}
              }}
              aria-label="Play"
              title="Play"
              data-testid="game-play-btn"
            >
              <PlayIcon size={15} weight="fill" />
            </Button>
          )}

          {/* How to play and light/dark live in the bar under the board now,
              beside Awards (Jack, 2026-10-19); the page asks us to open them. */}
          {!isLH && <HowToPlayDrawer open={helpOpen} onOpenChange={setHelpOpen} showTrigger={false} />}
          {!isLH && <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />}

          {/* Resign: ask for confirmation, then forfeit (the same for bot
              games and games against people). */}
          {raceOver ? (
            // A finished race: Race again in place of Resign (it opens the
            // pop-up, where the request waits for the other racer).
            <Button
              size="sm"
              className="h-7 rounded-full bg-[#0288d1] hover:bg-[#039be5] text-white font-bold gap-1 ml-1"
              onClick={() => { setGameOverOpen(true); if (rematchState === "idle") rematch(); }}
              aria-label="Race again"
              title="Race again"
              data-testid="race-again-top"
            >
              <ArrowClockwise size={14} weight="bold" /><span className="hidden sm:inline">Race again</span>
            </Button>
          ) : match.is_bot ? (
            <Button
              size="sm"
              className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground gap-1.5 ml-1"
              disabled={completed}
              data-testid="game-resign-btn"
              onClick={() => setResignModal({ open: true, exiting: false })}
            >
              <Flag size={15} weight="fill" />
              <span className="hidden sm:inline">Resign</span>
            </Button>
          ) : (
            // Against people the flag opens Resign and Offer draw.
            // Not modal, so the resign confirm can open straight from it.
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground gap-1.5 ml-1"
                  disabled={completed}
                  data-testid="game-resign-btn"
                >
                  <Flag size={15} weight="fill" />
                  <span className="hidden sm:inline">Resign</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={() => setResignModal({ open: true, exiting: false })}
                  className="gap-2 text-destructive focus:text-destructive"
                  data-testid="game-resign-item"
                >
                  <Flag size={16} weight="fill" /> Resign
                </DropdownMenuItem>
                {drawOfferFrom === user?.id ? (
                  <DropdownMenuItem
                    onClick={() => wsSendOrQueue({ action: "draw_cancel" })}
                    className="gap-2"
                    data-testid="game-draw-cancel"
                  >
                    <Handshake size={16} weight="fill" /> Withdraw draw offer
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    disabled={!!drawOfferFrom}
                    onClick={() => {
                      wsSendOrQueue({ action: "draw_offer" });
                      toast.success("Draw offered. Waiting for your opponent.");
                    }}
                    className="gap-2"
                    data-testid="game-draw-offer"
                  >
                    <Handshake size={16} weight="fill" /> Offer draw
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setHeaderFolded(true)}
            className="rounded-full w-7 h-7 -mr-2 text-muted-foreground hover:text-foreground"
            aria-label="Hide header"
            title="Hide header"
            data-testid="game-header-hide"
          >
            <CaretUp size={16} weight="bold" />
          </Button>
        </div>
      </header>

      {/* Folded header: one slim row with names and Elo; tap to open
          (Jack picked option B, 2026-10-19). */}
      {headerCollapsed && !isLH && !isStream && (
        <div className="flex-none h-7 px-3 flex items-center gap-2 text-[11px] border-b border-border bg-surface/80 z-10" data-testid="game-header-slim">
          {(match.status !== "active" || noTimeLimit || match.is_bot) && (
            <button
              type="button"
              onClick={() => navigate("/lobby")}
              className="text-muted-foreground hover:text-foreground -ml-1 p-1"
              aria-label={match.status === "active" ? "Games" : "Lobby"}
              data-testid="game-back-btn-slim"
            >
              <ArrowLeft size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setHeaderFolded(false)}
            className="flex flex-1 min-w-0 items-center gap-2 h-full text-left"
            aria-label={chatUnread ? `Show header, ${chatUnread} unread chat` : "Show header"}
            title="Show header"
            data-testid="game-header-show"
          >
            <span className="truncate min-w-0">
              <b className="text-brand">{me.username}</b>
              {isClock ? <span className="font-mono font-bold"> {formatClock(clockLeft(me.id) ?? 0)}</span>
                : me.elo ? <span className="text-muted-foreground"> {me.elo}</span> : null}
              <span className="text-muted-foreground/70 mx-1.5">vs</span>
              <b className="text-[hsl(var(--chart-3))]">{opp.username}</b>
              {isClock ? <span className="font-mono font-bold"> {formatClock(clockLeft(opp.id) ?? 0)}</span>
                : opp.elo ? <span className="text-muted-foreground"> {opp.elo}</span> : null}
            </span>
            {chatUnread > 0 && (
              <span className="w-2 h-2 rounded-full bg-destructive flex-none" data-testid="game-header-show-unread" />
            )}
            <CaretDown size={14} weight="bold" className="ml-auto flex-none text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Iframe fills the rest */}
      <div className="flex-1 min-h-0 bg-background relative" style={isLH ? { background: LH_PAGE_BG } : undefined}>
        <iframe
          ref={iframeRef}
          key={`${matchId}-${layout}`}
          src={(() => {
            const meIsP1 = match.player1_id === user?.id;
            const meName = (meIsP1 ? match.player1_username : match.player2_username) || "You";
            const meElo  = iframeEloRef.current.me;
            const oppName = (meIsP1 ? match.player2_username : match.player1_username) || "Opponent";
            const oppElo  = iframeEloRef.current.opp;
            const enc = (s) => encodeURIComponent(s);
            const baseQs = `layout=${layout}&theme=${iframeThemeRef.current}&mode=${match.mode}&mid=${enc(match.match_id || "")}`;
            // Always run the multiplayer-style UI chrome (compact CSS, base
            // labels, log relocation, AI→Opponent rename, mobile keyboard
            // hooks). For bot matches we add `&bot=1` so the iframe keeps
            // its local AI loop and skips the relay/peel-waiting wrappers.
            // Raider Races against a person: one shared board, and the
            // player who was challenged (player 2) takes the right-hand pad.
            const raceQs = layout === "race"
              ? "&race=1" + (race3 ? `&race3=1&raceslot=${race3.slot}` : meIsP1 ? "" : "&raceside=2")
              // Letterheist between people: its board-game look on its board.
              : layout === LH_LAYOUT ? "&lh=1" : "";
            // Stream (Jack, 2026-11-04): a game against a person in the Stream look.
            const uiQs = isStream ? "&ui=stream" : "";
            const mpQs = uiQs + raceQs + "&multiplayer=1" + (match.is_bot ? "&bot=1" : "") + (match.test_rules === "unbroken" ? "&rules=unbroken" : "") + (match.same_tiles ? "&same=1" : "") + (match.clock ? "&live=1" : "") + (match.is_bot && match.bot_first ? "&botfirst=1" : "") + (match.is_bot && match.simultaneous ? "&simul=1" + (match.clock ? `&clock=${encodeURIComponent(match.clock)}` : "") : "");
            const mobileQs = searchParams.get("mobilekb") === "1" ? "&mobilekb=1" : "";
            const labelQs = `&me_name=${enc(meName)}&me_elo=${meElo ?? ""}&opp_name=${enc(oppName)}&opp_elo=${oppElo ?? ""}`;
            // The demo game in the new player induction gets a guided tour
            // (Welcome.jsx marks the match on this device).
            let tutorialQs = "";
            try { if (match.is_bot && localStorage.getItem(`jangle:tutorial-match:${match.match_id}`)) tutorialQs = "&tutorial=1"; } catch { /* ignore */ }
            // ⚠️ Cache buster — Vercel/CDN aggressively caches /game/jangle.html
            // and clients can keep serving the stale file for hours after a
            // deploy. Bump JANGLE_BUILD any time jangle.html is meaningfully
            // changed (cut-through relay, mobile UX, validation, etc.) so the
            // new build loads immediately.
            const JANGLE_BUILD = "20261104-letters1";
            return `/game/jangle.html?v=${JANGLE_BUILD}&${baseQs}${mpQs}${mobileQs}${labelQs}${tutorialQs}`;
          })()}
          title="Jangle Game"
          className="absolute inset-0 w-full h-full border-0"
          data-testid="game-iframe"
          onLoad={() => {
            const w = iframeRef.current?.contentWindow;
            if (!w) return;
            // Re-apply the current theme in case it changed while loading.
            try { w.postMessage({ type: "jangle:theme", theme }, "*"); } catch { /* ignore */ }
            // The page says jangle:page_ready as soon as it listens, which
            // boots it straight away; this is the fallback for a page that
            // never says so (bootIframe runs once per page load).
            clearTimeout(bootTimerRef.current);
            bootTimerRef.current = setTimeout(() => bootIframeRef.current(w), 300);
          }}
        />

        {liveWaiting && !h2hOpen && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-[rgba(12,14,22,0.62)] backdrop-blur-[1.5px] px-4" data-testid="live-waiting">
            <div className="w-full max-w-[300px] rounded-[18px] border border-[rgba(255,138,61,0.5)] bg-[#161412] px-5 pt-5 pb-4 text-center text-[#eee] shadow-[0_14px_44px_rgba(0,0,0,0.55)]">
              <Avatar userId={opp.id} username={opp.username} className="w-[50px] h-[50px] text-lg mx-auto mb-2.5 border-2 border-[#555]"
                fallbackClassName="bg-[hsl(var(--chart-3))] text-background" />
              <div className="font-mono font-bold text-[13px] tracking-[2px] uppercase text-[#ff8a3d] break-words">
                Waiting for {opp.username || "your opponent"}{"\u2026"}
              </div>
              <p className="text-[13px] leading-snug text-[#c9c9c9] mt-2.5 mb-3">
                The game starts when you're both here.<br />Your clocks haven't started.
              </p>
              {oppOnline !== null && (
                <div className="inline-flex items-center gap-1.5 text-xs text-[#aaa] mb-4" data-testid="live-waiting-online">
                  <span className={`w-2 h-2 rounded-full ${oppOnline ? "bg-emerald-500" : "bg-[#777]"}`} />
                  {oppOnline ? "Online now" : "Offline right now"}
                </div>
              )}
              <button type="button" onClick={() => { markLiveGameLeft(match.match_id); navigate("/lobby"); }}
                className="block w-full rounded-full border border-[#555] bg-[#2a2826] py-2.5 text-sm font-semibold text-white hover:bg-[#34312e]"
                data-testid="live-waiting-lobby">
                {"\u2190"} Return to lobby
              </button>
              <div className="text-[11px] text-[#888] mt-2">The game keeps waiting. Rejoin or call it off from the lobby.</div>
            </div>
          </div>
        )}

        {/* WS connection indicator is now rendered in the header bar (out of
            the iframe / board / hand-strip area entirely). */}

        {/* Elo delta toast pill (after completion) */}
        {eloDelta !== null && (
          <div
            className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full bg-surface border border-border shadow-lg flex items-center gap-2 z-20"
            data-testid="elo-delta-pill"
          >
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Elo</span>
            <span className={`font-display font-bold ${eloDelta >= 0 ? "text-[hsl(var(--chart-4))]" : "text-destructive"}`}>
              {eloDelta >= 0 ? "+" : ""}{eloDelta}
            </span>
          </div>
        )}

        {!match.is_bot && (
          <ChatPanel
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            messages={chatMessages}
            myId={user?.id}
            oppName={(match.player1_id === user?.id ? match.player2_username : match.player1_username) || "Opponent"}
            connected={wsLive}
            onSend={(text) => {
              const ws = wsRef.current;
              if (!ws || ws.readyState !== 1) return false;
              try { ws.send(JSON.stringify({ action: "chat", text })); return true; } catch { return false; }
            }}
          />
        )}

        {/* React-level mobile-keyboard toggle removed — auto-open on cell
            tap is the only entry point now. */}
      </div>

      {/* The opponent offered a draw. */}
      {!completed && drawOfferFrom && drawOfferFrom !== user?.id && (
        <div
          className="fixed top-16 left-1/2 -translate-x-1/2 z-50 w-[min(92vw,360px)] rounded-2xl border border-border bg-background shadow-xl p-4"
          data-testid="draw-offer-card"
        >
          <p className="flex items-center gap-2 font-display font-bold">
            <Handshake size={20} weight="fill" className="text-brand" />
            {opp.username || "Your opponent"} offers a draw
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            If you accept, the game ends and both Elo ratings move as for a draw.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => wsSendOrQueue({ action: "draw_decline" })}
              data-testid="draw-decline-btn"
            >
              Decline
            </Button>
            <Button
              className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground"
              onClick={() => wsSendOrQueue({ action: "draw_accept" })}
              data-testid="draw-accept-btn"
            >
              Accept
            </Button>
          </div>
        </div>
      )}

      {/* Resign confirm modal */}
      <ResignConfirmModal
        open={resignModal.open}
        exiting={resignModal.exiting}
        onCancel={() => setResignModal({ open: false, exiting: false })}
        onConfirm={() => performResign(resignModal.exiting)}
      />

      <WinCelebration active={celebrate} />

      {/* Closed the result menu? This brings it back. Races against people
          reopen it from the result pill in the header instead. */}
      {completionInfo && !raceVsPeople && !gameOverOpen && !reviewOpen && !feedbackOpen && !captureHold && !streamerHold && (
        <DraggablePill
          onClick={() => setGameOverOpen(true)}
          title="Drag to move"
          className="z-40 inline-flex items-center gap-2 h-11 px-5 whitespace-nowrap rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold shadow-lg"
          data-testid="reopen-result"
        >
          {completionInfo.result === "win" ? <Trophy size={18} weight="fill" />
            : completionInfo.result === "draw" ? <Handshake size={18} weight="fill" /> : <Flag size={18} weight="fill" />}
          {completionInfo.result === "win" ? "Victory" : completionInfo.result === "draw" ? "Draw" : "Defeat"}
          <span className="font-normal opacity-80">· Show menu</span>
          <DotsSixVertical size={16} weight="bold" className="-mr-2 opacity-60" aria-hidden="true" />
        </DraggablePill>
      )}

      {racePodium && match.layout === "race" && (
        <RacePodium
          racers={[
            { name: user?.username || "You",
              you: true, character: user ? myCharacter(user) : null, rows: racePodium.rows.H, won: racePodium.winner === "H" },
            { name: (race3 ? race3.a.username : match.player1_id === user?.id ? match.player2_username : match.player1_username) || "Opponent",
              character: oppCharacter, rows: racePodium.rows.A, won: racePodium.winner === "A" },
            ...(race3 ? [{ name: race3.b.username || "Opponent", character: opp2Character, rows: racePodium.rows.B, won: racePodium.winner === "B" }] : []),
          ]}
          onDone={racePodiumDone}
        />
      )}

      {/* Raider Races against people: the race end pop-up (Jack, 2026-10-26). */}
      {raceVsPeople && completionInfo && gameOverOpen && !captureHold && !streamerHold && (
        <RaceResult
          fixed
          racers={raceRacers}
          round={raceEndRef.current.round}
          reason={completionInfo.winReason || ""}
          eloDelta={race3 ? null : eloDelta}
          onAgain={rematch}
          rematchState={rematchState}
          oppName={raceRacers[1]?.name || "Opponent"}
          onAccept={rematchAccept}
          onDecline={rematchDecline}
          onCancel={rematchCancel}
          onView={() => setGameOverOpen(false)}
          onLobby={mainMenu}
        />
      )}

      {/* Game-over screen — appears for BOTH players at match end */}
      {!match.is_bot && !raceVsPeople && (
        <GameOverModal
          open={gameOverOpen && !captureHold && !streamerHold}
          meCharacter={meCharacter}
          oppCharacter={oppCharacter}
          onOpenChange={setGameOverOpen}
          result={completionInfo?.result || "draw"}
          eloDelta={eloDelta}
          oppUsername={(match.player1_id === user?.id ? match.player2_username : match.player1_username) || "Opponent"}
          winReason={completionInfo?.winReason || ""}
          rematchState={rematchState}
          onReview={() => { setGameOverOpen(false); setReviewOpen(true); }}
          onRematch={rematch}
          onRematchAccept={rematchAccept}
          onRematchDecline={rematchDecline}
          onRematchCancel={rematchCancel}
          onNewGame={newGame}
          onMainMenu={mainMenu}
          isBot={false}
          race={match.layout === "race"}
          stats={match.layout === "race" ? null : gameStats}
          colours={resolveColours(myColour, oppColour)}
          matchId={matchId}
          meUsername={(match.player1_id === user?.id ? match.player1_username : match.player2_username) || user?.username}
        />
      )}

      {/* Bot matches: keep legacy feedback flow */}
      {match.is_bot && (
        <>
          <GameOverModal
            open={gameOverOpen && !captureHold && !streamerHold}
            meCharacter={meCharacter}
            oppCharacter={oppCharacter}
            onOpenChange={setGameOverOpen}
            result={completionInfo?.result || "draw"}
            eloDelta={eloDelta}
            oppUsername={match.player2_username}
            winReason={completionInfo?.winReason || ""}
            onReview={() => { setGameOverOpen(false); setFeedbackOpen(true); }}
            onRematch={rematch}
            onNewGame={newGame}
            onMainMenu={mainMenu}
            isBot={true}
            stats={gameStats}
            colours={resolveColours(myColour, oppColour)}
            matchId={matchId}
            meUsername={match.player1_username || user?.username}
          />
          <FeedbackModal
            open={feedbackOpen}
            onOpenChange={setFeedbackOpen}
            matchId={matchId}
            defaultLayout={layout}
            onDone={() => navigate("/lobby")}
          />
        </>
      )}

      {/* Step-through review (PvP) */}
      {!match.is_bot && (
        <ReviewModal
          dropLoose={isStream}
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          moves={movesRef.current}
          matchId={matchId}
          myId={user?.id}
          board={reviewBoard}
          meUsername={(match.player1_id === user?.id ? match.player1_username : match.player2_username) || "You"}
          oppUsername={(match.player1_id === user?.id ? match.player2_username : match.player1_username) || "Opponent"}
          eloDelta={eloDelta}
          winnerIsMe={completionInfo ? completionInfo.result === "win" : typeof eloDelta === "number" && eloDelta > 0}
          onClose={() => { setReviewOpen(false); setGameOverOpen(true); }}
        />
      )}
    </div>
  );
}
